import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type DragEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MarketingRepository } from '../../data/repositories';
import {
  HabitaActiveFilterChip,
  HabitaButton,
  HabitaChartTooltipSurface,
  HabitaDateRangeFilter,
  HabitaEmptyState,
  HabitaErrorNotice,
  HabitaSectionHeader,
  HabitaSelect,
  classNames,
  habitaControlBorderClass,
  habitaSurfaceClass,
  isHabitaDateInputValue,
  resolveHabitaDateRange,
} from '../../shared/components/habita';
import type { HabitaDateBasis, HabitaDatePeriod, HabitaDateRangeFilterValue } from '../../shared/components/habita';
import type {
  MarketingAuditResponse,
  MarketingDashboardData,
  MarketingDecisionMatrixResponse,
  MarketingFilterOptionsData,
  MarketingFetchParams,
  MarketingInterestRegionsResponse,
  MarketingOtherInterestLocality,
  MarketingOverviewResponse,
  MarketingRow,
  MarketingRowsResponse,
  MarketingSummary,
} from '../../shared/models';
import InterestRegionsTab, { OtherLocalitiesDialog } from './components/InterestRegionsTab';

type MarketingTab = 'overview' | 'campaigns' | 'ads' | 'audience' | 'interestRegions' | 'placement' | 'regions' | 'creativeRegions' | 'decision' | 'audit';
type MarketingTabLoadState = Partial<Record<MarketingTab, boolean>>;
type MarketingDashboardCacheSnapshot = {
  dashboard: MarketingDashboardData;
  loadedTabs: MarketingTabLoadState;
};
type MarketingFilterOptionsRequest = {
  key: string;
  params: Pick<MarketingFetchParams, 'date_basis' | 'endDate' | 'startDate'>;
};
type MarketingTrendMetric = 'custo_por_pasta_aprovada' | 'custo_por_pasta_enviada' | 'custo_por_venda';
type LeadTimeChartGranularity = 'daily' | 'monthly' | 'weekly';
type LeadTimeChartView = 'approval' | 'leadConversion';
type PerformanceRankingLevel = 'campaigns' | 'adsets' | 'ads' | 'creatives' | 'forms';
type CampaignInterestRegionColumnKey =
  | 'interestRegionsAll'
  | 'interestRegionOther'
  | 'interestRegionSamambaia'
  | 'interestRegionAnapolis'
  | 'interestRegionValparaiso'
  | 'interestRegionAguasLindas'
  | 'interestRegionLuziania'
  | 'interestRegionNovoGama'
  | 'interestRegionSantoAntonio';
type CampaignColumnKey =
  | 'entity'
  | 'spend'
  | 'impressions'
  | 'uniqueClicks'
  | 'ctr'
  | 'messages'
  | 'messageCost'
  | 'cpfReceived'
  | 'cpfReceivedCost'
  | 'cleanCpf'
  | 'cleanCpfCost'
  | 'sentFolders'
  | 'approvedFolders'
  | 'appointments'
  | 'avgSentTime'
  | 'medianSentTime'
  | 'avgApprovedTime'
  | 'medianApprovedTime'
  | 'sentCost'
  | 'approvedCost'
  | 'purchases'
  | 'purchaseCost'
  | 'recommendation'
  | CampaignInterestRegionColumnKey;

type CampaignAggregationScope = 'adset' | 'campaign' | 'creative';

interface MarketingFilterState {
  adId: string;
  adsetId: string;
  accountId: string;
  age: string;
  campaignId: string;
  creativeId: string;
  creativeStatus: string;
  customEnd: string;
  customStart: string;
  devicePlatform: string;
  formId: string;
  gender: string;
  period: HabitaDatePeriod;
  platformPosition: string;
  publisherPlatform: string;
  region: string;
  sourceChannel: string;
}

interface SelectOption {
  label: string;
  value: string;
}

interface TableColumn {
  align?: 'left' | 'right';
  className?: string;
  label: string;
  render: (row: MarketingRow) => ReactNode;
}

interface MarketingChartPoint {
  date: string;
  gasto: number;
  label: string;
  metricValue: number | null;
}

interface MarketingChartTooltipPayload {
  color?: string;
  dataKey?: string | number;
  name?: string | number;
  value?: string | number | null;
}

interface AudienceBarPoint {
  approvedLabel: string;
  cost: number;
  fill: string;
  gasto: unknown;
  name: string;
  row: MarketingRow;
}

interface AudienceBarTooltipPayload extends MarketingChartTooltipPayload {
  payload?: AudienceBarPoint;
}

interface LeadTimeChartPoint {
  average: number | null;
  count: number;
  countLabel: string;
  label: string;
  max: number | null;
  median: number | null;
  min: number | null;
  periodLabel: string;
}

interface LeadTimeChartTooltipPayload extends MarketingChartTooltipPayload {
  payload?: LeadTimeChartPoint;
}

interface LeadTimeXAxisTickProps {
  payload?: {
    value?: string | number;
  };
  periodLabels: Map<string, string>;
  x?: string | number;
  y?: string | number;
}

interface MetricComparison {
  label: string;
  tone: 'neutral' | 'positive' | 'negative';
}

interface MarketingFilterOptionGroups {
  accountId: SelectOption[];
  adId: SelectOption[];
  adsetId: SelectOption[];
  age: SelectOption[];
  campaignId: SelectOption[];
  creativeId: SelectOption[];
  devicePlatform: SelectOption[];
  formId: SelectOption[];
  gender: SelectOption[];
  platformPosition: SelectOption[];
  publisherPlatform: SelectOption[];
  region: SelectOption[];
  sourceChannel: SelectOption[];
}

interface ActiveFilterItem {
  key: keyof MarketingFilterState;
  label: string;
  value: string;
}

const ALL_VALUE = '__all__';
const DEFAULT_LIMIT = 100;
const MARKETING_DASHBOARD_CACHE_PREFIX = 'habita4u.marketing.dashboard.v8.';
const MARKETING_DATE_FILTER_STORAGE_KEY = 'habita4u.marketing.dateFilter';
const MARKETING_DATE_BASIS_STORAGE_KEY = 'habita4u.marketing.dateBasis';

const dateFilterPeriods: HabitaDatePeriod[] = [
  'today',
  'yesterday',
  '7d',
  '30d',
  '90d',
  'month',
  'previous_month',
  'previous_quarter',
  'year',
  'custom',
];

const creativeStatusOptions: SelectOption[] = [
  { label: 'Ativo', value: 'active' },
  { label: 'Inativo', value: 'inactive' },
];

const contactSourceOptions: SelectOption[] = [
  { label: 'Click to WhatsApp', value: 'ctwa' },
  { label: 'Lead Ads', value: 'lead_ads' },
];

const campaignInterestRegionColumns: Array<{
  city: string | null;
  key: CampaignInterestRegionColumnKey;
  label: string;
}> = [
  { city: null, key: 'interestRegionsAll', label: 'Todas as Regiões de Interesse' },
  { city: null, key: 'interestRegionOther', label: 'Outras localidades' },
  { city: 'Samambaia, DF', key: 'interestRegionSamambaia', label: 'Samambaia, DF' },
  { city: 'Anápolis, GO', key: 'interestRegionAnapolis', label: 'Anápolis, GO' },
  { city: 'Valparaíso de Goiás, GO', key: 'interestRegionValparaiso', label: 'Valparaíso de Goiás, GO' },
  { city: 'Águas Lindas, GO', key: 'interestRegionAguasLindas', label: 'Águas Lindas, GO' },
  { city: 'Luziânia, GO', key: 'interestRegionLuziania', label: 'Luziânia, GO' },
  { city: 'Novo Gama, GO', key: 'interestRegionNovoGama', label: 'Novo Gama, GO' },
  { city: 'Santo Antônio do Descoberto, GO', key: 'interestRegionSantoAntonio', label: 'Santo Antônio do Descoberto, GO' },
];
const campaignInterestRegionColumnByKey = new Map(
  campaignInterestRegionColumns.map((column) => [column.key, column]),
);
const campaignInterestRegionColumnKeys = new Set<CampaignColumnKey>(
  campaignInterestRegionColumns.map((column) => column.key),
);

const campaignTableColumns: { key: CampaignColumnKey; label: string }[] = [
  { key: 'entity', label: 'Campanha / Adset / Criativo' },
  { key: 'spend', label: 'Gasto' },
  { key: 'impressions', label: 'Impressões' },
  { key: 'uniqueClicks', label: 'Cliques Únicos' },
  { key: 'ctr', label: 'CTR' },
  { key: 'messages', label: 'Contatos' },
  ...campaignInterestRegionColumns.map(({ key, label }) => ({ key, label })),
  { key: 'messageCost', label: 'Custo por Contato' },
  { key: 'cpfReceived', label: 'CPF Recebido' },
  { key: 'cpfReceivedCost', label: 'Custo por CPF Recebido' },
  { key: 'cleanCpf', label: 'CPF Limpo' },
  { key: 'cleanCpfCost', label: 'Custo por CPF Limpo' },
  { key: 'sentFolders', label: 'Pastas Enviadas' },
  { key: 'sentCost', label: 'Custo/Pasta Enviada' },
  { key: 'avgSentTime', label: 'Média Tempo Pasta Enviada' },
  { key: 'medianSentTime', label: 'Mediana Tempo Pasta Enviada' },
  { key: 'approvedFolders', label: 'Pastas Aprovadas' },
  { key: 'approvedCost', label: 'Custo/Pasta Aprovada' },
  { key: 'avgApprovedTime', label: 'Média Tempo Pasta Aprovada' },
  { key: 'medianApprovedTime', label: 'Mediana Tempo Pasta Aprovada' },
  { key: 'appointments', label: 'Agendamento Presencial' },
  { key: 'purchases', label: 'Vendas' },
  { key: 'purchaseCost', label: 'Custo/Venda' },
  { key: 'recommendation', label: 'Recomendação' },
];

const defaultCampaignColumnOrderKeys = campaignTableColumns.map((column) => column.key);
const defaultCampaignColumnKeys = campaignTableColumns
  .filter((column) => (
    column.key !== 'recommendation'
    && !campaignInterestRegionColumnKeys.has(column.key)
  ))
  .map((column) => column.key);
const campaignColumnByKey = new Map(campaignTableColumns.map((column) => [column.key, column]));
const campaignEntityColumnWidthClass = 'w-[clamp(320px,32vw,420px)] min-w-[320px] max-w-[420px]';

const tabs: { icon: string; key: MarketingTab; label: string }[] = [
  { key: 'overview', label: 'Visão Geral', icon: 'ri-dashboard-line' },
  { key: 'campaigns', label: 'Campanhas e Adsets', icon: 'ri-stack-line' },
  { key: 'ads', label: 'Anúncios e Criativos', icon: 'ri-megaphone-line' },
  { key: 'audience', label: 'Público', icon: 'ri-group-line' },
  { key: 'interestRegions', label: 'Regiões de interesse', icon: 'ri-map-pin-line' },
  { key: 'placement', label: 'Posicionamentos', icon: 'ri-layout-masonry-line' },
];

const initialFilters: MarketingFilterState = {
  adId: '',
  adsetId: '',
  accountId: '',
  age: '',
  campaignId: '',
  creativeId: '',
  creativeStatus: '',
  customEnd: '',
  customStart: '',
  devicePlatform: '',
  formId: '',
  gender: '',
  period: '30d',
  platformPosition: '',
  publisherPlatform: '',
  region: '',
  sourceChannel: '',
};

function resolveDateRange(filters: MarketingFilterState): Pick<MarketingFetchParams, 'startDate' | 'endDate'> {
  return resolveHabitaDateRange(filters);
}

function savedMarketingDateFilter(): HabitaDateRangeFilterValue | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return null;

  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(MARKETING_DATE_FILTER_STORAGE_KEY) ?? 'null') as Partial<MarketingFilterState> | null;
    const savedPeriod = parsed?.period;
    if (!savedPeriod || !dateFilterPeriods.includes(savedPeriod)) return null;

    if (savedPeriod === 'custom') {
      if (!isHabitaDateInputValue(parsed?.customStart) || !isHabitaDateInputValue(parsed?.customEnd)) return null;
      if (parsed.customStart > parsed.customEnd) return null;
      return {
        customEnd: parsed.customEnd,
        customStart: parsed.customStart,
        period: 'custom',
      };
    }

    return {
      customEnd: '',
      customStart: '',
      period: savedPeriod,
    };
  } catch {
    return null;
  }
}

function initialMarketingFilters(): MarketingFilterState {
  return {
    ...initialFilters,
    ...savedMarketingDateFilter(),
  };
}

function savedMarketingDateBasis(): HabitaDateBasis {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return 'creation';

  const saved = window.sessionStorage.getItem(MARKETING_DATE_BASIS_STORAGE_KEY);
  return saved === 'stage' || saved === 'status' ? saved : 'creation';
}

function emptyToUndefined(value: string): string | undefined {
  return value.trim() ? value.trim() : undefined;
}

function splitFormFilterValue(value: string): { formId?: string; formKey?: string } {
  const text = emptyToUndefined(value);
  if (!text) return {};

  const separatorIndex = text.indexOf(':');
  if (separatorIndex > 0 && separatorIndex < text.length - 1) {
    return {
      formId: text.slice(separatorIndex + 1),
      formKey: text,
    };
  }

  return { formId: text };
}

function buildFetchParams(filters: MarketingFilterState, dateBasis: HabitaDateBasis): MarketingFetchParams {
  const formFilter = splitFormFilterValue(filters.formId);
  return {
    ...resolveDateRange(filters),
    date_basis: dateBasis,
    limit: DEFAULT_LIMIT,
    account_id: emptyToUndefined(filters.accountId),
    ad_id: emptyToUndefined(filters.adId),
    adset_id: emptyToUndefined(filters.adsetId),
    age: emptyToUndefined(filters.age),
    campaign_id: emptyToUndefined(filters.campaignId),
    creative_id: emptyToUndefined(filters.creativeId),
    creative_status: emptyToUndefined(filters.creativeStatus),
    device_platform: emptyToUndefined(filters.devicePlatform),
    form_id: formFilter.formId,
    form_key: formFilter.formKey,
    gender: emptyToUndefined(filters.gender),
    platform_position: emptyToUndefined(filters.platformPosition),
    publisher_platform: emptyToUndefined(filters.publisherPlatform),
    region: emptyToUndefined(filters.region),
    source_channel: emptyToUndefined(filters.sourceChannel),
  };
}

function dateLabel(value: string | undefined): string {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function numberValue(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function textValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function rowText(row: MarketingRow, key: string): string {
  return textValue(row[key]);
}

function formatMoney(value: unknown, fallback = 'Sem pasta'): string {
  const numeric = optionalNumber(value);
  if (numeric === null) return fallback;

  return numeric.toLocaleString('pt-BR', {
    currency: 'BRL',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  });
}

function formatNumber(value: unknown, digits = 0): string {
  const numeric = numberValue(value);
  return numeric.toLocaleString('pt-BR', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

function formatOptionalNumber(value: unknown, digits = 0, fallback = '-'): string {
  const numeric = optionalNumber(value);
  if (numeric === null) return fallback;

  return numeric.toLocaleString('pt-BR', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

function formatPercent(value: unknown): string {
  const numeric = optionalNumber(value);
  return numeric === null ? '-' : `${formatNumber(numeric, 2)}%`;
}

function conversionPercent(numerator: unknown, denominator: unknown): number | null {
  const numeratorValue = optionalNumber(numerator);
  const denominatorValue = optionalNumber(denominator);
  if (numeratorValue === null || denominatorValue === null || denominatorValue <= 0) return null;
  return (numeratorValue / denominatorValue) * 100;
}

function conversionRateInfo(numerator: unknown, denominator: unknown): { label: string; title: string } {
  const numeratorValue = optionalNumber(numerator);
  const denominatorValue = optionalNumber(denominator);

  if (numeratorValue === null || denominatorValue === null) {
    return {
      label: '-',
      title: 'Conversão indisponível porque uma das etapas ainda não veio na resposta da API.',
    };
  }

  if (denominatorValue <= 0) {
    return {
      label: '-',
      title: 'Conversão não calculada porque a etapa anterior está zerada no período.',
    };
  }

  return {
    label: formatPercent((numeratorValue / denominatorValue) * 100),
    title: `${formatOptionalNumber(numeratorValue)} de ${formatOptionalNumber(denominatorValue)}`,
  };
}

function costPer(total: unknown, quantity: unknown): number | null {
  const totalValue = optionalNumber(total);
  const quantityValue = optionalNumber(quantity);
  if (totalValue === null || quantityValue === null || quantityValue <= 0) return null;
  return totalValue / quantityValue;
}

function metricComparison(
  currentValue: unknown,
  previousValue: unknown,
  improvement: 'increase' | 'decrease' | 'neutral' = 'increase'
): MetricComparison | undefined {
  const current = optionalNumber(currentValue);
  const previous = optionalNumber(previousValue);
  if (current === null || previous === null || previous <= 0) return undefined;

  const change = ((current - previous) / previous) * 100;
  if (!Number.isFinite(change)) return undefined;

  const isPositive = improvement === 'increase'
    ? change >= 0
    : improvement === 'decrease'
      ? change <= 0
      : false;

  return {
    label: `${change >= 0 ? '+' : ''}${formatPercent(change)} vs período anterior`,
    tone: improvement === 'neutral' || Math.abs(change) < 0.005 ? 'neutral' : isPositive ? 'positive' : 'negative',
  };
}

function parseTimestamp(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const timestamp = new Date(String(value)).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

function minValue(values: number[]): number {
  return Math.min(...values);
}

function maxValue(values: number[]): number {
  return Math.max(...values);
}

function roundedDays(value: number | null): number | null {
  return value === null ? null : Number(value.toFixed(2));
}

function prefixedMetricKey(scope: CampaignAggregationScope, key: string): string {
  return `${scope}_${key}`;
}

function timeMetricCandidateKeys(scope: CampaignAggregationScope, key: string): string[] {
  const scopedKey = prefixedMetricKey(scope, key);
  return scope === 'creative' ? [scopedKey, key] : [scopedKey];
}

function firstOptionalNumber(rows: MarketingRow[], keys: string[]): number | null {
  for (const row of rows) {
    for (const key of keys) {
      const value = optionalNumber(row[key]);
      if (value !== null) return value;
    }
  }
  return null;
}

function optionalNumberValues(rows: MarketingRow[], keys: string[]): number[] {
  return rows.flatMap((row) => (
    keys
      .map((key) => optionalNumber(row[key]))
      .filter((value): value is number => value !== null)
  ));
}

const timeBaseKeysByScope: Record<CampaignAggregationScope, string[]> = {
  campaign: ['campaign_create_time', 'campaign_created_time', 'campaign_created_at', 'campaign_start_time'],
  adset: ['adset_create_time', 'adset_created_time', 'adset_created_at', 'adset_start_time'],
  creative: ['creative_create_time', 'creative_created_time', 'creative_created_at', 'ad_created_time', 'created_time', 'create_time'],
};

const sentFolderTimeEventKeys = [
  'pasta_enviada_at',
  'pasta_enviada_em',
  'folder_sent_at',
  'sent_folder_at',
  'sent_at',
  'initiate_checkout_at',
];

const approvedFolderTimeEventKeys = [
  'pasta_aprovada_at',
  'pasta_aprovada_em',
  'folder_approved_at',
  'approved_folder_at',
  'approved_at',
  'qualified_lead_at',
  'qualified_at',
];

function firstTimestamp(row: MarketingRow, keys: string[]): number | null {
  for (const key of keys) {
    const timestamp = parseTimestamp(row[key]);
    if (timestamp !== null) return timestamp;
  }
  return null;
}

function durationDaysFromRow(row: MarketingRow, scope: CampaignAggregationScope, eventKeys: string[]): number | null {
  const baseTimestamp = firstTimestamp(row, timeBaseKeysByScope[scope]);
  const eventTimestamp = firstTimestamp(row, eventKeys);
  if (baseTimestamp === null || eventTimestamp === null || eventTimestamp < baseTimestamp) return null;
  return (eventTimestamp - baseTimestamp) / 86400000;
}

function durationSamples(rows: MarketingRow[], scope: CampaignAggregationScope, eventKeys: string[]): number[] {
  return rows
    .map((row) => durationDaysFromRow(row, scope, eventKeys))
    .filter((value): value is number => value !== null);
}

function calculateTimeMetric(
  rows: MarketingRow[],
  scope: CampaignAggregationScope,
  key: string,
  eventKeys: string[],
  reducer: (values: number[]) => number | null
): number | null {
  const providedValue = firstOptionalNumber(rows, timeMetricCandidateKeys(scope, key));
  if (providedValue !== null) return providedValue;
  return roundedDays(reducer(durationSamples(rows, scope, eventKeys)));
}

function calculateTimeExtreme(
  rows: MarketingRow[],
  scope: CampaignAggregationScope,
  key: string,
  eventKeys: string[],
  reducer: (values: number[]) => number
): number | null {
  const providedValues = optionalNumberValues(rows, timeMetricCandidateKeys(scope, key));
  if (providedValues.length > 0) return roundedDays(reducer(providedValues));
  const samples = durationSamples(rows, scope, eventKeys);
  return samples.length === 0 ? null : roundedDays(reducer(samples));
}

function applyTimeMetrics(aggregate: MarketingRow, rows: MarketingRow[], scope: CampaignAggregationScope) {
  aggregate.tempo_medio_pasta_enviada_dias = calculateTimeMetric(rows, scope, 'tempo_medio_pasta_enviada_dias', sentFolderTimeEventKeys, average);
  aggregate.mediana_tempo_pasta_enviada_dias = calculateTimeMetric(rows, scope, 'mediana_tempo_pasta_enviada_dias', sentFolderTimeEventKeys, median);
  aggregate.menor_tempo_pasta_enviada_dias = calculateTimeExtreme(rows, scope, 'menor_tempo_pasta_enviada_dias', sentFolderTimeEventKeys, minValue);
  aggregate.maior_tempo_pasta_enviada_dias = calculateTimeExtreme(rows, scope, 'maior_tempo_pasta_enviada_dias', sentFolderTimeEventKeys, maxValue);
  aggregate.tempo_medio_pasta_aprovada_dias = calculateTimeMetric(rows, scope, 'tempo_medio_pasta_aprovada_dias', approvedFolderTimeEventKeys, average);
  aggregate.mediana_tempo_pasta_aprovada_dias = calculateTimeMetric(rows, scope, 'mediana_tempo_pasta_aprovada_dias', approvedFolderTimeEventKeys, median);
  aggregate.menor_tempo_pasta_aprovada_dias = calculateTimeExtreme(rows, scope, 'menor_tempo_pasta_aprovada_dias', approvedFolderTimeEventKeys, minValue);
  aggregate.maior_tempo_pasta_aprovada_dias = calculateTimeExtreme(rows, scope, 'maior_tempo_pasta_aprovada_dias', approvedFolderTimeEventKeys, maxValue);
}

function formatDurationDays(value: unknown): string {
  const numeric = optionalNumber(value);
  if (numeric === null) return '-';
  if (numeric < 1) return `${formatNumber(numeric * 24, 1)} h`;
  return `${formatNumber(numeric, numeric < 10 ? 1 : 0)} d`;
}

function formatDurationDaysOnly(value: unknown): string {
  const numeric = optionalNumber(value);
  return numeric === null ? '-' : `${formatNumber(numeric, 1)}d`;
}

function formatDurationPair(averageValue: unknown, medianValue: unknown): string {
  const averageLabel = formatDurationDaysOnly(averageValue);
  const medianLabel = formatDurationDaysOnly(medianValue);
  return averageLabel === '-' && medianLabel === '-' ? '-' : `${averageLabel} / ${medianLabel}`;
}

function approvedCost(row: MarketingRow): number | null {
  return optionalNumber(row.custo_por_pasta_aprovada)
    ?? optionalNumber(row.custo_estimado_por_pasta_aprovada)
    ?? costPer(row.gasto, row.pastas_aprovadas ?? row.pastas_aprovadas_estimadas);
}

function sentCost(row: MarketingRow): number | null {
  return optionalNumber(row.custo_por_pasta_enviada)
    ?? optionalNumber(row.custo_estimado_por_pasta_enviada)
    ?? costPer(row.gasto, row.pastas_enviadas ?? row.pastas_enviadas_estimadas);
}

function contactValue(row: MarketingRow): unknown {
  return row.contatos ?? row.contacts ?? row.first_messages ?? row.first_contacts;
}

function messageCost(row: MarketingRow): number | null {
  return optionalNumber(row.custo_por_contato)
    ?? costPer(row.gasto, contactValue(row));
}

function cpfReceivedCost(row: MarketingRow): number | null {
  return optionalNumber(row.custo_por_cpf_recebido)
    ?? costPer(row.gasto, row.lead_com_cpf);
}

function cleanCpfCost(row: MarketingRow): number | null {
  return optionalNumber(row.custo_por_cpf_limpo)
    ?? costPer(row.gasto, row.lead_com_cpf_limpo);
}

function purchasesValue(row: MarketingRow): unknown {
  return row.purchases ?? row.vendas ?? row.purchase;
}

function sentFoldersValue(row: MarketingRow): unknown {
  return row.pastas_enviadas ?? row.pastas_enviadas_estimadas;
}

function approvedFoldersValue(row: MarketingRow): unknown {
  return row.pastas_aprovadas ?? row.pastas_aprovadas_estimadas;
}

function purchaseCost(row: MarketingRow): number | null {
  return optionalNumber(row.custo_por_venda)
    ?? optionalNumber(row.custo_venda)
    ?? costPer(row.gasto, purchasesValue(row));
}

function firstText(row: MarketingRow, keys: string[], fallback = '-'): string {
  for (const key of keys) {
    const value = textValue(row[key]);
    if (value !== '-') return value;
  }
  return fallback;
}

function formatDateTime(value: unknown): string {
  const text = textValue(value);
  if (text === '-') return text;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

function formatDateOnly(value: unknown): string {
  const text = textValue(value);
  if (text === '-') return text;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function creativeName(row: MarketingRow): string {
  return firstText(row, ['creative_name', 'creative_title', 'ad_name', 'creative_id']);
}

function creativeBody(row: MarketingRow): string {
  return firstText(row, ['creative_body', 'body']);
}

function creativePreviewUrl(row: MarketingRow): string {
  const url = firstText(row, ['preview_shareable_link', 'preview_sharable_link'], '');
  return url === '-' ? '' : url;
}

function creativeCreatedAt(row: MarketingRow): string {
  return formatDateTime(firstText(row, ['create_time', 'created_time', 'ad_created_time']));
}

function creativeQuality(row: MarketingRow): string {
  return firstText(row, ['quality_ranking', 'quality_rank']);
}

function creativeConversationRate(row: MarketingRow): string {
  return firstText(row, ['conversation_rate', 'conversion_rate_ranking', 'conversion_rate']);
}

type MarketingEntityLevel = 'adset' | 'campaign' | 'creative';
type MarketingEntityStatus = 'active' | 'inactive' | 'unknown';

const marketingStatusKeysByLevel: Record<MarketingEntityLevel, string[]> = {
  campaign: ['campaign_effective_status', 'campaign_status'],
  adset: ['adset_effective_status', 'adset_status'],
  creative: ['ad_effective_status', 'ad_effective_stattus', 'effective_status', 'effective_stattus', 'ad_status', 'status'],
};

function marketingEntityRawStatus(row: MarketingRow, level: MarketingEntityLevel): string {
  return firstText(row, marketingStatusKeysByLevel[level], '').trim().toUpperCase();
}

function marketingEntityStatus(row: MarketingRow, level: MarketingEntityLevel): MarketingEntityStatus {
  const rawStatus = marketingEntityRawStatus(row, level);
  if (!rawStatus) return 'unknown';
  return ['ACTIVE', 'ATIVO'].includes(rawStatus) ? 'active' : 'inactive';
}

function marketingEntityStatusLabel(row: MarketingRow, level: MarketingEntityLevel): string {
  const rawStatus = marketingEntityRawStatus(row, level);
  const feminine = level === 'campaign';
  const labels: Record<string, string> = {
    ACTIVE: feminine ? 'Ativa' : 'Ativo',
    ATIVO: feminine ? 'Ativa' : 'Ativo',
    ARCHIVED: feminine ? 'Arquivada' : 'Arquivado',
    CAMPAIGN_PAUSED: feminine ? 'Pausada' : 'Pausado',
    ADSET_PAUSED: feminine ? 'Pausada' : 'Pausado',
    DELETED: feminine ? 'Excluída' : 'Excluído',
    DISAPPROVED: feminine ? 'Reprovada' : 'Reprovado',
    IN_PROCESS: 'Em análise',
    PAUSED: feminine ? 'Pausada' : 'Pausado',
    PENDING_REVIEW: 'Em revisão',
    PREAPPROVED: feminine ? 'Pré-aprovada' : 'Pré-aprovado',
    WITH_ISSUES: 'Com problemas',
  };

  if (!rawStatus) return 'Sem status';
  return labels[rawStatus] ?? (feminine ? 'Inativa' : 'Inativo');
}

function marketingStatusBadgeTone(row: MarketingRow, level: MarketingEntityLevel): string {
  const status = marketingEntityStatus(row, level);
  const rawStatus = marketingEntityRawStatus(row, level);
  if (status === 'active') return 'border-emerald-300/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200';
  if (status === 'unknown') return 'border-amber-300/50 bg-amber-500/10 text-amber-800 dark:text-amber-100';
  if (['DELETED', 'DISAPPROVED', 'WITH_ISSUES'].includes(rawStatus)) {
    return 'border-rose-300/50 bg-rose-500/10 text-rose-700 dark:text-rose-200';
  }
  if (['IN_PROCESS', 'PENDING_REVIEW'].includes(rawStatus)) {
    return 'border-amber-300/50 bg-amber-500/10 text-amber-800 dark:text-amber-100';
  }
  return 'border-slate-300/60 bg-slate-500/10 text-slate-700 dark:text-slate-200';
}

function marketingStatusDotTone(row: MarketingRow, level: MarketingEntityLevel): string {
  const status = marketingEntityStatus(row, level);
  const rawStatus = marketingEntityRawStatus(row, level);
  if (status === 'active') return 'bg-emerald-500';
  if (status === 'unknown') return 'bg-amber-500';
  if (['DELETED', 'DISAPPROVED', 'WITH_ISSUES'].includes(rawStatus)) return 'bg-rose-500';
  if (['IN_PROCESS', 'PENDING_REVIEW'].includes(rawStatus)) return 'bg-amber-500';
  return 'bg-slate-500';
}

function marketingEntityStatusCountLabel(row: MarketingRow, level: MarketingEntityLevel, count: number): string {
  const rawStatus = marketingEntityRawStatus(row, level);
  const singular = count === 1;
  const labels: Record<string, [string, string]> = {
    ACTIVE: ['ativo', 'ativos'],
    ATIVO: ['ativo', 'ativos'],
    ARCHIVED: ['arquivado', 'arquivados'],
    CAMPAIGN_PAUSED: ['pausado', 'pausados'],
    ADSET_PAUSED: ['pausado', 'pausados'],
    DELETED: ['excluído', 'excluídos'],
    DISAPPROVED: ['reprovado', 'reprovados'],
    IN_PROCESS: ['em análise', 'em análise'],
    PAUSED: ['pausado', 'pausados'],
    PENDING_REVIEW: ['em revisão', 'em revisão'],
    PREAPPROVED: ['pré-aprovado', 'pré-aprovados'],
    WITH_ISSUES: ['com problema', 'com problemas'],
  };

  if (!rawStatus) return 'sem status';
  const label = labels[rawStatus] ?? ['inativo', 'inativos'];
  return singular ? label[0] : label[1];
}

function MarketingEntityStatusBadge({ level, row }: { level: MarketingEntityLevel; row: MarketingRow }) {
  return (
    <span className={classNames(
      'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 font-instrument text-[10px] font-semibold',
      marketingStatusBadgeTone(row, level)
    )}>
      <span className={classNames('h-1.5 w-1.5 rounded-full', marketingStatusDotTone(row, level))} aria-hidden />
      {marketingEntityStatusLabel(row, level)}
    </span>
  );
}

function CreativeStatusBadge({ row }: { row: MarketingRow }) {
  return <MarketingEntityStatusBadge level="creative" row={row} />;
}

function CreativePreviewLink({ row }: { row: MarketingRow }) {
  const url = creativePreviewUrl(row);
  if (!url) return <span className="text-textSecondary">Sem preview</span>;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      Preview
      <i className="ri-external-link-line text-xs" aria-hidden />
    </a>
  );
}

function heatColor(value: number | null, min: number, max: number): string {
  if (value === null) return 'hsl(var(--muted))';
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return 'hsl(145 60% 55%)';
  const ratio = Math.min(Math.max((value - min) / (max - min), 0), 1);
  return `hsl(${145 - ratio * 145} 60% 55%)`;
}

function rowApprovedCostLabel(row: MarketingRow): string {
  return formatMoney(approvedCost(row));
}

function recommendationGroup(row: MarketingRow): 'Escalar' | 'Escalar com cautela' | 'Revisar' | 'Reduzir' | 'Pausar' {
  const normalized = rowText(row, 'acao_recomendada').toLowerCase();
  if (normalized.includes('aumentar') || normalized.includes('escalar')) return 'Escalar';
  if (normalized.includes('cautela')) return 'Escalar com cautela';
  if (normalized.includes('reduzir')) return 'Reduzir';
  if (normalized.includes('pausar')) return 'Pausar';
  return 'Revisar';
}

function actionClassName(action: string): string {
  const normalized = action.toLowerCase();
  if (normalized.includes('aumentar verba')) return 'border-emerald-300/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200';
  if (normalized.includes('cautela')) return 'border-sky-300/40 bg-sky-500/10 text-sky-700 dark:text-sky-200';
  if (normalized.includes('reduzir')) return 'border-amber-300/40 bg-amber-500/10 text-amber-700 dark:text-amber-200';
  if (normalized.includes('pausar')) return 'border-red-300/40 bg-red-500/10 text-red-700 dark:text-red-200';
  if (normalized.includes('revisar')) return 'border-purple-300/40 bg-purple-500/10 text-purple-700 dark:text-purple-200';
  return 'border-border-subtle bg-muted text-textSecondary';
}

function ActionBadge({ action }: { action: unknown }) {
  const label = textValue(action);

  return (
    <span className={classNames('inline-flex whitespace-nowrap rounded-full border px-2 py-1 font-instrument text-[11px] font-medium', actionClassName(label))}>
      {label}
    </span>
  );
}

function optionsFromRows(
  rows: MarketingRow[],
  valueKey: string,
  labelKey?: string,
  selectedValue?: string,
  labelFormatter?: (label: string, value: string) => string,
  includeSelectedValue = true
): SelectOption[] {
  const map = new Map<string, string>();

  rows.forEach((row) => {
    const value = String(row[valueKey] ?? '').trim();
    if (!value) return;
    const label = labelKey ? String(row[labelKey] ?? value).trim() : value;
    const displayLabel = label || value;
    map.set(value, labelFormatter ? labelFormatter(displayLabel, value) : displayLabel);
  });

  if (includeSelectedValue && selectedValue && selectedValue !== ALL_VALUE && !map.has(selectedValue)) {
    map.set(selectedValue, labelFormatter ? labelFormatter(selectedValue, selectedValue) : selectedValue);
  }

  return Array.from(map.entries())
    .sort(([, left], [, right]) => left.localeCompare(right, 'pt-BR'))
    .map(([value, label]) => ({ value, label }));
}

function optionsWithDefault(label: string, options: SelectOption[]): SelectOption[] {
  return [{ label, value: ALL_VALUE }, ...options];
}

function selectedFilterValue(value: string): string {
  return value || ALL_VALUE;
}

function filterValueFromSelect(value: string): string {
  return value === ALL_VALUE ? '' : value;
}

function relationValue(row: MarketingRow, key: string): string {
  return String(row[key] ?? '').trim();
}

function rowsMatchingRelations(rows: MarketingRow[], relations: Array<[string, string]>): MarketingRow[] {
  const usableRelations = relations.filter(([key, value]) => {
    if (!value) return false;
    return rows.some((row) => relationValue(row, key) !== '');
  });
  const usableRelationKeys = new Set(usableRelations.map(([key]) => key));

  if (usableRelations.length === 0) return rows;

  return rows.filter((row) => relations.every(([key, value]) => {
    if (!value || !usableRelationKeys.has(key)) return true;
    return relationValue(row, key) === value;
  }));
}

function rowWithFormFilterValue(row: MarketingRow): MarketingRow {
  const formKey = String(row.form_key ?? '').trim();
  const formId = String(row.form_id ?? '').trim();
  return {
    ...row,
    form_filter_value: formKey || formId,
  };
}

function selectedOptionLabel(options: SelectOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function buildActiveFilterItems(filters: MarketingFilterState, options: MarketingFilterOptionGroups): ActiveFilterItem[] {
  const items: ActiveFilterItem[] = [];

  const optionFilters: Array<{ key: keyof MarketingFilterOptionGroups & keyof MarketingFilterState; label: string; options: SelectOption[] }> = [
    { key: 'accountId', label: 'Conta', options: options.accountId },
    { key: 'campaignId', label: 'Campanha', options: options.campaignId },
    { key: 'adsetId', label: 'Adset', options: options.adsetId },
    { key: 'adId', label: 'Anúncio', options: options.adId },
    { key: 'creativeId', label: 'Criativo', options: options.creativeId },
    { key: 'sourceChannel', label: 'Fonte', options: options.sourceChannel },
    { key: 'formId', label: 'Formulário', options: options.formId },
    { key: 'region', label: 'Região', options: options.region },
    { key: 'publisherPlatform', label: 'Plataforma', options: options.publisherPlatform },
    { key: 'platformPosition', label: 'Posicionamento', options: options.platformPosition },
    { key: 'age', label: 'Idade', options: options.age },
    { key: 'gender', label: 'Gênero', options: options.gender },
  ];

  optionFilters.forEach(({ key, label, options: filterOptions }) => {
    const value = filters[key];
    if (!value) return;
    items.push({
      key,
      label,
      value: selectedOptionLabel(filterOptions, value),
    });
  });

  if (filters.creativeStatus) {
    items.push({
      key: 'creativeStatus',
      label: 'Status criativo',
      value: selectedOptionLabel(creativeStatusOptions, filters.creativeStatus),
    });
  }

  return items;
}

function translateUnknownLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (['unknown', 'unknow', 'não identificado', 'nao identificado', 'not specified', 'not_specified'].includes(normalized)) {
    return 'Não identificado';
  }
  return value;
}

function translateGenderLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (['female', 'famale', 'feminino'].includes(normalized)) return 'Feminino';
  if (['male', 'masculino'].includes(normalized)) return 'Masculino';
  return translateUnknownLabel(value);
}

function translateAgeLabel(value: string): string {
  return translateUnknownLabel(value);
}

function safeRowKey(row: MarketingRow, index: number): string {
  const identity = String(row.ad_id ?? row.adset_id ?? row.campaign_id ?? row.creative_id ?? row.region ?? row.age ?? 'row');
  return `${identity}-${index}`;
}

function summarizeDecisionRows(matrix: MarketingDecisionMatrixResponse): MarketingRow[] {
  return [
    ...matrix.campaigns,
    ...matrix.adsets,
    ...matrix.ads,
    ...matrix.regions,
    ...matrix.age_gender,
    ...matrix.placement,
  ];
}

function MetricCard({
  className,
  comparison,
  description,
  highlight = false,
  icon,
  label,
  tone = 'primary',
  tooltip,
  value,
}: {
  className?: string;
  comparison?: MetricComparison;
  description?: string;
  highlight?: boolean;
  icon: string;
  label: string;
  tone?: 'primary' | 'success' | 'warning' | 'info';
  tooltip?: string;
  value: string;
}) {
  const toneClass = {
    info: 'text-sky-600',
    primary: 'text-primary',
    success: 'text-emerald-700 dark:text-emerald-300',
    warning: 'text-amber-700 dark:text-amber-300',
  }[tone];
  const comparisonClass = comparison?.tone === 'positive'
    ? 'text-emerald-700 dark:text-emerald-300'
    : comparison?.tone === 'negative'
      ? 'text-red-700 dark:text-red-300'
      : 'text-textSecondary';

  return (
    <article
      className={classNames(
        habitaSurfaceClass,
        'border-l-4 p-5',
        highlight ? 'border-l-primary bg-brand-soft/40' : 'border-l-primary/40',
        className
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1 font-instrument text-xs font-semibold uppercase tracking-wide text-textSecondary">
            <span>{label}</span>
            {tooltip && <MetricTooltip content={tooltip} label={label} />}
          </p>
          <p className={classNames('mt-2 font-instrument font-semibold text-textPrimary', highlight ? 'text-3xl' : 'text-2xl')}>{value}</p>
          {description && <p className="mt-1 truncate font-instrument text-xs text-textSecondary">{description}</p>}
          {comparison && (
            <p className={classNames('mt-2 font-instrument text-xs font-medium', comparisonClass)}>
              {comparison.label}
            </p>
          )}
        </div>
        <i className={`${icon} shrink-0 text-base ${highlight ? 'text-primary' : toneClass}`} aria-hidden />
      </div>
    </article>
  );
}

function MetricTooltip({ content, label }: { content: string; label: string }) {
  const lines = content.split('\n').filter(Boolean);

  return (
    <span className="group/metric-tooltip relative inline-flex">
      <span
        tabIndex={0}
        className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-emerald-500/25 bg-emerald-50 text-[11px] text-emerald-700 outline-none transition-colors hover:border-emerald-500/45 hover:bg-emerald-100 focus:ring-2 focus:ring-emerald-500/20 dark:border-emerald-300/20 dark:bg-emerald-300/10 dark:text-emerald-200"
        aria-label={`Detalhes de ${label}`}
      >
        <i className="ri-information-line" aria-hidden />
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 hidden w-64 -translate-x-1/2 rounded-lg border border-emerald-100 bg-white p-3 text-left font-instrument text-xs normal-case leading-5 tracking-normal text-slate-700 shadow-xl shadow-slate-900/10 group-hover/metric-tooltip:block group-focus-within/metric-tooltip:block dark:border-emerald-300/20 dark:bg-slate-950 dark:text-slate-100"
      >
        {lines.map((line) => {
          const [name, ...rest] = line.split(':');
          const value = rest.join(':').trim();

          return (
            <span key={line} className="block">
              {value ? (
                <>
                  <span className="font-semibold text-slate-900 dark:text-white">{name}:</span> {value}
                </>
              ) : line}
            </span>
          );
        })}
      </span>
    </span>
  );
}

function EstimatedNotice({ notice }: { notice?: string }) {
  if (!notice) return null;

  return (
    <div className="rounded-lg border border-sky-300/40 bg-sky-500/10 px-4 py-3 font-instrument text-sm text-sky-800 dark:text-sky-100">
      <i className="ri-sparkling-line mr-2" aria-hidden />
      {notice}
    </div>
  );
}

function MarketingTable({
  columns,
  emptyDescription,
  emptyTitle,
  rows,
}: {
  columns: TableColumn[];
  emptyDescription: string;
  emptyTitle: string;
  rows: MarketingRow[];
}) {
  if (rows.length === 0) {
    return (
      <HabitaEmptyState
        icon="ri-inbox-line"
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  return (
    <div className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse font-instrument text-sm">
          <thead className={`border-b ${habitaControlBorderClass} bg-muted text-xs text-textSecondary`}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.label}
                  className={classNames(
                    'whitespace-nowrap px-4 py-2.5 font-medium',
                    column.align === 'right' ? 'text-right' : 'text-left',
                    column.className
                  )}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {rows.map((row, index) => (
              <tr key={safeRowKey(row, index)} className="transition-colors hover:bg-accent">
                {columns.map((column) => (
                  <td
                    key={column.label}
                    className={classNames(
                      'px-4 py-3 align-top',
                      column.align === 'right' ? 'text-right' : 'text-left',
                      column.className
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function axisMoney(value: unknown): string {
  const numeric = numberValue(value);
  if (Math.abs(numeric) >= 1000) return `R$ ${formatNumber(numeric / 1000, 1)}k`;
  return `R$ ${formatNumber(numeric)}`;
}

function trendMetricValue(row: MarketingRow, metric: MarketingTrendMetric): number | null {
  if (metric === 'custo_por_venda') return purchaseCost(row);
  if (metric === 'custo_por_pasta_enviada') return sentCost(row);
  return approvedCost(row);
}

function buildChartData(rows: MarketingRow[], metric: MarketingTrendMetric): MarketingChartPoint[] {
  return rows.map((row) => {
    const date = rowText(row, 'date_start');
    return {
      date,
      gasto: numberValue(row.gasto),
      label: dateLabel(date),
      metricValue: trendMetricValue(row, metric),
    };
  });
}

function MarketingChartTooltip({
  active,
  label,
  metricLabel,
  payload,
}: {
  active?: boolean;
  label?: string | number;
  metricLabel: string;
  payload?: MarketingChartTooltipPayload[];
}) {
  if (!active || !payload?.length) return null;

  const spend = payload.find((item) => item.dataKey === 'gasto')?.value;
  const metric = payload.find((item) => item.dataKey === 'metricValue')?.value;

  return (
    <HabitaChartTooltipSurface>
      <p className="mb-1 font-semibold">{label}</p>
      <p className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-primary" />
        Gasto: <span className="font-mono font-semibold">{formatMoney(spend, 'R$ 0,00')}</span>
      </p>
      <p className="mt-1 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-sky-500" />
        {metricLabel}: <span className="font-mono font-semibold">{formatMoney(metric)}</span>
      </p>
    </HabitaChartTooltipSurface>
  );
}

function MarketingEvolutionChart({
  data,
  metric,
  metricLabel,
}: {
  data: MarketingRow[];
  metric: MarketingTrendMetric;
  metricLabel: string;
}) {
  const chartData = useMemo(() => buildChartData(data, metric), [data, metric]);

  if (chartData.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-lg border border-border-subtle bg-muted/35 font-instrument text-sm text-textSecondary">
        Sem dados diários para o período.
      </div>
    );
  }

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ bottom: 0, left: -8, right: 8, top: 8 }}>
          <defs>
            <linearGradient id="marketingSpendGradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.38} />
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis
            axisLine={false}
            dataKey="label"
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            tickFormatter={axisMoney}
            tickLine={false}
            width={62}
            yAxisId="spend"
          />
          <YAxis
            axisLine={false}
            orientation="right"
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            tickFormatter={axisMoney}
            tickLine={false}
            width={62}
            yAxisId="metric"
          />
          <Tooltip content={<MarketingChartTooltip metricLabel={metricLabel} />} />
          <Area
            dataKey="gasto"
            fill="url(#marketingSpendGradient)"
            name="Gasto (R$)"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            type="monotone"
            yAxisId="spend"
          />
          <Line
            connectNulls
            dataKey="metricValue"
            dot={false}
            name={`${metricLabel} (R$)`}
            stroke="rgb(14 165 233)"
            strokeWidth={2}
            type="monotone"
            yAxisId="metric"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function axisDurationDays(value: unknown): string {
  const numeric = numberValue(value);
  return `${formatNumber(numeric, numeric < 10 ? 1 : 0)}d`;
}

function leadTimeChartRows(overview: MarketingOverviewResponse, granularity: LeadTimeChartGranularity): MarketingRow[] {
  return overview.time_series?.[granularity] ?? [];
}

function parseDateParts(value: string): { day: number; month: number; year: number } | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return { day, month, year };
}

function isoWeekNumber(value: string): number | null {
  const parts = parseDateParts(value);
  if (!parts) return null;

  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function monthShortLabel(value: string): string {
  const parts = parseDateParts(value);
  if (!parts) return dateLabel(value);

  return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString('pt-BR', {
    month: 'short',
    year: 'numeric',
  });
}

function leadTimePeriodLabel(row: MarketingRow, granularity: LeadTimeChartGranularity): string {
  const start = rowText(row, 'period_start');
  const end = rowText(row, 'period_end');
  if (granularity === 'daily') return dateLabel(start);
  if (start && end && start !== end) return `${dateLabel(start)} até ${dateLabel(end)}`;
  return dateLabel(start);
}

function leadTimeXAxisLabel(row: MarketingRow, granularity: LeadTimeChartGranularity): string {
  const start = rowText(row, 'period_start');
  if (granularity === 'daily') return dateLabel(start);
  if (granularity === 'monthly') return monthShortLabel(start);

  const weekNumber = isoWeekNumber(start);
  return weekNumber === null ? dateLabel(start) : `S${weekNumber}`;
}

const leadTimeChartConfig: Record<LeadTimeChartView, {
  averageKey: string;
  countKey: string;
  countLabel: string;
  emptyLabel: string;
  maxKey: string;
  medianKey: string;
  minKey: string;
  title: string;
}> = {
  approval: {
    averageKey: 'tempo_medio_pasta_aprovada_dias',
    countKey: 'pastas_aprovadas',
    countLabel: 'Pastas aprovadas',
    emptyLabel: 'Sem pastas aprovadas para compor a série.',
    maxKey: 'maior_tempo_pasta_aprovada_dias',
    medianKey: 'mediana_tempo_pasta_aprovada_dias',
    minKey: 'menor_tempo_pasta_aprovada_dias',
    title: 'Tempo até Pasta Aprovada',
  },
  leadConversion: {
    averageKey: 'tempo_medio_conversao_lead_dias',
    countKey: 'leads_convertidos',
    countLabel: 'Leads convertidos',
    emptyLabel: 'Sem leads convertidos para compor a série.',
    maxKey: 'maior_tempo_conversao_lead_dias',
    medianKey: 'mediana_tempo_conversao_lead_dias',
    minKey: 'menor_tempo_conversao_lead_dias',
    title: 'Tempo até Conversão do Lead',
  },
};

function buildLeadTimeChartData(
  rows: MarketingRow[],
  view: LeadTimeChartView,
  granularity: LeadTimeChartGranularity
): LeadTimeChartPoint[] {
  const config = leadTimeChartConfig[view];

  return rows.map((row) => ({
    average: optionalNumber(row[config.averageKey]),
    count: numberValue(row[config.countKey]),
    countLabel: config.countLabel,
    label: leadTimeXAxisLabel(row, granularity),
    max: optionalNumber(row[config.maxKey]),
    median: optionalNumber(row[config.medianKey]),
    min: optionalNumber(row[config.minKey]),
    periodLabel: leadTimePeriodLabel(row, granularity),
  }));
}

function LeadTimeChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: LeadTimeChartTooltipPayload[];
}) {
  if (!active || !payload?.length) return null;

  const point = payload.find((item) => item.payload)?.payload;
  if (!point) return null;

  return (
    <HabitaChartTooltipSurface>
      <p className="mb-2 font-semibold">{point.periodLabel}</p>
      <p className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-primary" />
        Média: <span className="font-mono font-semibold">{formatDurationDaysOnly(point.average)}</span>
      </p>
      <p className="mt-1 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-sky-500" />
        Mediana: <span className="font-mono font-semibold">{formatDurationDaysOnly(point.median)}</span>
      </p>
      <p className="mt-2 text-textSecondary">Menor tempo: <span className="font-mono text-textPrimary">{formatDurationDaysOnly(point.min)}</span></p>
      <p className="mt-1 text-textSecondary">Maior tempo: <span className="font-mono text-textPrimary">{formatDurationDaysOnly(point.max)}</span></p>
      <p className="mt-1 text-textSecondary">{point.countLabel}: <span className="font-mono text-textPrimary">{formatNumber(point.count)}</span></p>
    </HabitaChartTooltipSurface>
  );
}

function LeadTimeXAxisTick({ payload, periodLabels, x, y }: LeadTimeXAxisTickProps) {
  const label = String(payload?.value ?? '');
  const xPosition = Number(x);
  const yPosition = Number(y);
  if (!label || !Number.isFinite(xPosition) || !Number.isFinite(yPosition)) return null;

  return (
    <text fill="hsl(var(--muted-foreground))" fontSize={12} textAnchor="middle" x={xPosition} y={yPosition + 16}>
      <title>{periodLabels.get(label) ?? label}</title>
      {label}
    </text>
  );
}

function LeadTimeEvolutionChart({
  granularity,
  overview,
  view,
}: {
  granularity: LeadTimeChartGranularity;
  overview: MarketingOverviewResponse;
  view: LeadTimeChartView;
}) {
  const rows = leadTimeChartRows(overview, granularity);
  const chartData = useMemo(() => buildLeadTimeChartData(rows, view, granularity), [granularity, rows, view]);
  const tickPeriodLabels = useMemo(
    () => new Map(chartData.map((point) => [point.label, point.periodLabel])),
    [chartData]
  );
  const hasVisibleSamples = chartData.some((point) => point.average !== null || point.median !== null);

  if (!hasVisibleSamples) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-lg border border-border-subtle bg-muted/35 px-4 text-center font-instrument text-sm text-textSecondary">
        {overview.data_kind === 'estimated'
          ? 'Os tempos de conversão não são exibidos para filtros estimados de público, região ou posicionamento.'
          : leadTimeChartConfig[view].emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ bottom: 0, left: -8, right: 14, top: 8 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="label"
              tick={(props) => <LeadTimeXAxisTick {...props} periodLabels={tickPeriodLabels} />}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              tickFormatter={axisDurationDays}
              tickLine={false}
              width={62}
            />
            <Tooltip content={<LeadTimeChartTooltip />} />
            <Line
              connectNulls
              dataKey="average"
              dot={{ r: 3 }}
              name="Média"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              type="monotone"
            />
            <Line
              connectNulls
              dataKey="median"
              dot={{ r: 3 }}
              name="Mediana"
              stroke="rgb(14 165 233)"
              strokeDasharray="5 4"
              strokeWidth={2}
              type="monotone"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap items-center gap-4 px-1 font-instrument text-xs text-textSecondary">
        <span className="inline-flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary" />
          Média
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-sky-500" />
          Mediana
        </span>
      </div>
    </div>
  );
}

function RankingList({
  costKey,
  emptyLabel,
  labelKey,
  rows,
  subtitle,
  title,
}: {
  costKey: string;
  emptyLabel: string;
  labelKey: string;
  rows: MarketingRow[];
  subtitle: (row: MarketingRow) => string;
  title: string;
}) {
  return (
    <section className={classNames(habitaSurfaceClass, 'h-full')}>
      <HabitaSectionHeader title={title} titleClassName="text-base" />
      <div className="space-y-1 px-3 pb-3">
        {rows.length === 0 ? (
          <p className="p-4 font-instrument text-sm text-textSecondary">{emptyLabel}</p>
        ) : (
          rows.slice(0, 8).map((row, index) => (
            <div key={safeRowKey(row, index)} className="flex items-center justify-between gap-3 rounded-md p-2 hover:bg-accent">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted font-instrument text-xs font-semibold">
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p className="truncate font-instrument text-sm font-medium text-textPrimary">{rowText(row, labelKey)}</p>
                  <p className="truncate font-instrument text-xs text-textSecondary">{subtitle(row)}</p>
                </div>
              </div>
              <span className="shrink-0 font-mono text-xs font-semibold text-textPrimary">{formatMoney(row[costKey])}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

const performanceRankingOptions: SelectOption[] = [
  { label: 'Campanhas', value: 'campaigns' },
  { label: 'Adsets', value: 'adsets' },
  { label: 'Anúncios', value: 'ads' },
  { label: 'Criativos', value: 'creatives' },
  { label: 'Formulários', value: 'forms' },
];

const performanceRankingEmptyLabels: Record<PerformanceRankingLevel, string> = {
  ads: 'Nenhum anúncio encontrado.',
  adsets: 'Nenhum adset encontrado.',
  campaigns: 'Nenhuma campanha encontrada.',
  creatives: 'Nenhum criativo encontrado.',
  forms: 'Nenhum formulário encontrado.',
};

function compareRankingRows(left: MarketingRow, right: MarketingRow): number {
  const leftCost = approvedCost(left);
  const rightCost = approvedCost(right);

  if (leftCost === null && rightCost !== null) return 1;
  if (leftCost !== null && rightCost === null) return -1;
  if (leftCost !== null && rightCost !== null && leftCost !== rightCost) return leftCost - rightCost;

  const approvedDifference = numberValue(approvedFoldersValue(right)) - numberValue(approvedFoldersValue(left));
  if (approvedDifference !== 0) return approvedDifference;

  return numberValue(right.gasto) - numberValue(left.gasto);
}

function rankingCreatedAt(row: MarketingRow, level: PerformanceRankingLevel): string {
  const keysByLevel: Record<PerformanceRankingLevel, string[]> = {
    ads: ['create_time', 'ad_created_time', 'created_time'],
    adsets: ['adset_create_time', 'adset_created_time', 'adset_created_at'],
    campaigns: ['campaign_create_time', 'campaign_created_time', 'campaign_created_at'],
    creatives: ['creative_create_time', 'creative_created_time', 'creative_created_at', 'create_time', 'ad_created_time', 'created_time'],
    forms: ['form_created_time', 'form_created_at', 'meta_created_at', 'created_at'],
  };

  return formatDateOnly(firstText(row, keysByLevel[level]));
}

function rankingPrimaryLabel(row: MarketingRow, level: PerformanceRankingLevel): string {
  if (level === 'campaigns') return firstText(row, ['campaign_name', 'campaign_id']);
  if (level === 'adsets') return firstText(row, ['adset_name', 'adset_id']);
  if (level === 'ads') return firstText(row, ['ad_name', 'ad_id']);
  if (level === 'forms') return firstText(row, ['form_name', 'form_id', 'form_key']);
  return firstText(row, ['creative_name', 'creative_title', 'creative_id']);
}

function rankingContext(row: MarketingRow, level: PerformanceRankingLevel): string {
  return rankingParentLines(row, level).join(' · ');
}

function rankingParentLines(row: MarketingRow, level: PerformanceRankingLevel): string[] {
  const campaign = firstText(row, ['campaign_name', 'campaign_id']);
  const adset = firstText(row, ['adset_name', 'adset_id']);
  const ad = firstText(row, ['ad_name', 'ad_id']);
  const lines: string[] = [];

  if (level === 'forms') {
    const source = firstText(row, ['source_channel_label', 'source_label', 'source_channel']);
    const campaignCount = optionalNumber(row.campaign_count);
    const adCount = optionalNumber(row.ad_count);

    lines.push(`Fonte: ${source === 'lead_ads' ? 'Lead Ads' : source}`);

    if (campaignCount === 1 && campaign !== '-') {
      lines.push(`Campanha: ${campaign}`);
    } else if (campaignCount !== null && campaignCount > 0) {
      lines.push(`${formatNumber(campaignCount)} ${campaignCount === 1 ? 'campanha' : 'campanhas'}`);
    }

    if (adCount === 1 && ad !== '-') {
      lines.push(`Anúncio: ${ad}`);
    } else if (adCount !== null && adCount > 0) {
      lines.push(`${formatNumber(adCount)} ${adCount === 1 ? 'anúncio' : 'anúncios'}`);
    }

    return lines.filter((line) => !line.endsWith(': -'));
  }

  if (level !== 'campaigns' && campaign !== '-') lines.push(`Campanha: ${campaign}`);
  if ((level === 'ads' || level === 'creatives') && adset !== '-') lines.push(`Adset: ${adset}`);
  if (level === 'creatives' && ad !== '-') lines.push(`Anúncio: ${ad}`);

  return lines;
}

function approvedFoldersLabel(row: MarketingRow): string {
  const value = approvedFoldersValue(row);
  const digits = optionalNumber(row.pastas_aprovadas) === null && optionalNumber(row.pastas_aprovadas_estimadas) !== null ? 2 : 0;
  return formatNumber(value, digits);
}

function approvedFoldersMetaLabel(row: MarketingRow): string {
  const numeric = numberValue(approvedFoldersValue(row));
  return `${approvedFoldersLabel(row)} ${numeric === 1 ? 'aprovada' : 'aprovadas'}`;
}

function performanceRankingRowKey(row: MarketingRow, level: PerformanceRankingLevel, index: number): string {
  const keysByLevel: Record<PerformanceRankingLevel, string[]> = {
    ads: ['campaign_id', 'adset_id', 'ad_id'],
    adsets: ['campaign_id', 'adset_id'],
    campaigns: ['campaign_id'],
    creatives: ['campaign_id', 'adset_id', 'ad_id', 'creative_id'],
    forms: ['form_key', 'page_id', 'form_id'],
  };
  const identity = keysByLevel[level]
    .map((key) => textValue(row[key]))
    .filter((value) => value !== '-')
    .join('::');

  return identity ? `${level}-${identity}-${index}` : `${level}-${safeRowKey(row, index)}`;
}

function RankingMetaItem({ icon, label }: { icon: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <i className={icon} aria-hidden />
      {label}
    </span>
  );
}

function PerformanceRankingList({
  onRequestRanking,
  overview,
  rankingLoadingLevel = null,
}: {
  onRequestRanking?: (level: PerformanceRankingLevel) => void;
  overview: MarketingOverviewResponse;
  rankingLoadingLevel?: PerformanceRankingLevel | null;
}) {
  const [level, setLevel] = useState<PerformanceRankingLevel>('campaigns');
  const rowsByLevel: Record<PerformanceRankingLevel, MarketingRow[]> = {
    ads: overview.ads,
    adsets: overview.adsets,
    campaigns: overview.campaigns,
    creatives: overview.creatives,
    forms: overview.forms,
  };
  const rowsCount = rowsByLevel[level]?.length ?? 0;
  const isLazyRankingLevel = level !== 'campaigns';
  const isLoadingCurrentLevel = rankingLoadingLevel === level;
  const rows = useMemo(
    () => [...(rowsByLevel[level] ?? [])].sort(compareRankingRows).slice(0, 5),
    [level, overview.ads, overview.adsets, overview.campaigns, overview.creatives, overview.forms]
  );

  useEffect(() => {
    if (isLazyRankingLevel && rowsCount === 0 && !isLoadingCurrentLevel) {
      onRequestRanking?.(level);
    }
  }, [isLazyRankingLevel, isLoadingCurrentLevel, level, onRequestRanking, rowsCount]);

  function handleLevelChange(event: ChangeEvent<HTMLSelectElement>) {
    const nextLevel = event.target.value as PerformanceRankingLevel;
    setLevel(nextLevel);
    if (nextLevel !== 'campaigns' && (rowsByLevel[nextLevel]?.length ?? 0) === 0) {
      onRequestRanking?.(nextLevel);
    }
  }

  return (
    <section className={classNames(habitaSurfaceClass, 'h-full')}>
      <HabitaSectionHeader title="Ranking por desempenho" titleClassName="text-base">
        <HabitaSelect
          value={level}
          onChange={handleLevelChange}
          options={performanceRankingOptions}
          className="w-40"
          aria-label="Nível do ranking de desempenho"
        />
      </HabitaSectionHeader>
      <div className="space-y-1 px-3 pb-3">
        {isLoadingCurrentLevel ? (
          <p className="p-4 font-instrument text-sm text-textSecondary">
            Carregando {performanceRankingOptions.find((option) => option.value === level)?.label.toLowerCase() ?? 'ranking'}...
          </p>
        ) : rows.length === 0 ? (
          <p className="p-4 font-instrument text-sm text-textSecondary">{performanceRankingEmptyLabels[level]}</p>
        ) : (
          rows.map((row, index) => {
            const cost = approvedCost(row);
            const createdAt = rankingCreatedAt(row, level);
            const createdLabel = level === 'campaigns' ? 'Criada' : 'Criado';
            const context = rankingContext(row, level);

            return (
              <div key={performanceRankingRowKey(row, level, index)} className="flex items-center justify-between gap-4 rounded-md border-b border-border-subtle/70 px-1 py-3 last:border-b-0 hover:bg-accent">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted font-instrument text-xs font-semibold text-textPrimary">
                    {index + 1}
                  </span>
                  <div className="min-w-0 space-y-1">
                    <p className="line-clamp-2 font-instrument text-sm font-semibold leading-snug text-textPrimary">{rankingPrimaryLabel(row, level)}</p>
                    {context ? <p className="line-clamp-2 font-instrument text-xs text-textSecondary">{context}</p> : null}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-instrument text-[11px] text-textSecondary">
                      <RankingMetaItem icon="ri-folder-line" label={approvedFoldersMetaLabel(row)} />
                      <span aria-hidden>•</span>
                      <RankingMetaItem icon="ri-coins-line" label={`${formatMoney(row.gasto, 'R$ 0,00')} investidos`} />
                      {createdAt !== '-' ? (
                        <>
                          <span aria-hidden>•</span>
                          <RankingMetaItem icon="ri-calendar-line" label={`${createdLabel} em ${createdAt}`} />
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-mono text-sm font-bold text-textPrimary">{cost === null ? 'Sem pasta' : formatMoney(cost)}</p>
                  <p className="font-instrument text-[11px] text-textSecondary">{cost === null ? '-' : 'por aprovação'}</p>
                </div>
              </div>
            );
          })
        )}
        <div className="flex items-center justify-center gap-2 border-t border-border-subtle pt-3 font-instrument text-xs text-textSecondary">
          <i className="ri-information-line" aria-hidden />
          <span>Ordenado por: Custo por Pasta Aprovada (menor valor primeiro)</span>
        </div>
      </div>
    </section>
  );
}

function summaryContactValue(summary: MarketingSummary): unknown {
  return summary.contatos ?? summary.first_messages;
}

function summaryLeadWithCpfValue(summary: MarketingSummary): unknown {
  return summary.lead_com_cpf;
}

function summaryCleanCpfValue(summary: MarketingSummary): unknown {
  return summary.lead_com_cpf_limpo;
}

function summaryMessageCost(summary: MarketingSummary): number | null {
  return optionalNumber(summary.custo_por_contato)
    ?? costPer(summary.gasto, summaryContactValue(summary));
}

function summaryCpfReceivedCost(summary: MarketingSummary): number | null {
  return optionalNumber(summary.custo_por_cpf_recebido)
    ?? costPer(summary.gasto, summaryLeadWithCpfValue(summary));
}

function summaryCleanCpfCost(summary: MarketingSummary): number | null {
  return optionalNumber(summary.custo_por_cpf_limpo)
    ?? costPer(summary.gasto, summaryCleanCpfValue(summary));
}

function summaryPurchaseCost(summary: MarketingSummary): number | null {
  return optionalNumber(summary.custo_por_venda) ?? costPer(summary.gasto, summary.purchases);
}

const estimatedTimeMetricTooltip = [
  'Tempo indisponível para este recorte.',
  'Filtros de público, região ou posicionamento usam distribuição estimada da Meta.',
  'Por isso não há uma amostra real de leads para calcular média, mediana, menor e maior tempo.',
].join('\n');

function timeMetricTooltip(summary: MarketingSummary, type: 'sent' | 'approved'): string {
  const prefix = type === 'sent' ? 'pasta_enviada' : 'pasta_aprovada';

  return [
    `Média: ${formatDurationDaysOnly(summary[`tempo_medio_${prefix}_dias` as keyof MarketingSummary])}`,
    `Mediana: ${formatDurationDaysOnly(summary[`mediana_tempo_${prefix}_dias` as keyof MarketingSummary])}`,
    `Menor tempo registrado: ${formatDurationDaysOnly(summary[`menor_tempo_${prefix}_dias` as keyof MarketingSummary])}`,
    `Maior tempo registrado: ${formatDurationDaysOnly(summary[`maior_tempo_${prefix}_dias` as keyof MarketingSummary])}`,
  ].join('\n');
}

function OverviewMetricSection({
  children,
  description,
  gridClassName = 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5',
  title,
}: {
  children: ReactNode;
  description: string;
  gridClassName?: string;
  title: string;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-fraunces text-lg font-semibold text-textPrimary">{title}</h2>
        <p className="mt-1 font-instrument text-sm text-textSecondary">{description}</p>
      </div>
      <div className={gridClassName}>
        {children}
      </div>
    </section>
  );
}

type ConversionFlowStep = {
  description: string;
  eventName: string;
  icon: string;
  label: string;
  note?: string;
  tone: 'primary' | 'success' | 'warning' | 'info';
  tooltip?: string;
  value: unknown;
};

function ConversionFlowSection({ steps }: { steps: ConversionFlowStep[] }) {
  return (
    <section className="space-y-3">
      <h2 className="font-instrument text-xs font-semibold uppercase tracking-wide text-textSecondary">
        Funil de Marketing
      </h2>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        {steps.map((step, index) => {
          const previousStep = steps[index - 1];
          return (
            <div key={step.label} className="flex items-center gap-3 lg:flex-1">
              {previousStep ? (
                <ConversionFlowConnector
                  fromValue={previousStep.value}
                  toValue={step.value}
                />
              ) : null}
              <ConversionFlowStepCard step={step} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ConversionFlowStepCard({ step }: { step: ConversionFlowStep }) {
  const toneStyles = {
    info: {
      border: 'border-l-sky-500/50',
      icon: 'text-sky-600 dark:text-sky-300',
    },
    primary: {
      border: 'border-l-primary/50',
      icon: 'text-primary',
    },
    success: {
      border: 'border-l-emerald-500/50',
      icon: 'text-emerald-700 dark:text-emerald-300',
    },
    warning: {
      border: 'border-l-amber-500/50',
      icon: 'text-amber-700 dark:text-amber-300',
    },
  }[step.tone];

  return (
    <article
      className={classNames(
        habitaSurfaceClass,
        'flex min-h-[112px] min-w-0 flex-1 flex-col justify-center border-l-4 p-4',
        toneStyles.border
      )}
      title={step.tooltip ?? `${step.description}.`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 font-instrument text-xs font-semibold uppercase tracking-wide text-textSecondary">
          {step.label}
        </span>
        <i className={`${step.icon} shrink-0 text-base ${toneStyles.icon}`} aria-hidden />
      </div>
      <div className="mt-2 font-instrument text-2xl font-semibold text-textPrimary">
        {formatOptionalNumber(step.value)}
      </div>
      {step.note ? (
        <div className="mt-1 inline-flex items-center gap-1 font-instrument text-[11px] font-medium text-amber-700 dark:text-amber-300">
          <i className="ri-information-line" aria-hidden />
          <span>{step.note}</span>
        </div>
      ) : null}
    </article>
  );
}

function ConversionFlowConnector({ fromValue, toValue }: { fromValue: unknown; toValue: unknown }) {
  const rate = conversionRateInfo(toValue, fromValue);

  return (
    <div className="flex shrink-0 flex-col items-center text-textSecondary" title={rate.title}>
      <i className="ri-arrow-right-s-line hidden text-base lg:block" aria-hidden />
      <div className="flex items-center gap-1 lg:block lg:text-center">
        <i className="ri-arrow-down-s-line text-base lg:hidden" aria-hidden />
        <span className="font-instrument text-[11px] font-medium">{rate.label}</span>
      </div>
    </div>
  );
}

function OverviewTab({
  onRequestRanking,
  overview,
  rankingLoadingLevel = null,
}: {
  onRequestRanking?: (level: PerformanceRankingLevel) => void;
  overview: MarketingOverviewResponse;
  rankingLoadingLevel?: PerformanceRankingLevel | null;
}) {
  const summary: MarketingSummary = overview.summary ?? {};
  const previousSummary: MarketingSummary = overview.previous_summary ?? {};
  const [trendMetric, setTrendMetric] = useState<MarketingTrendMetric>('custo_por_pasta_aprovada');
  const [leadTimeView, setLeadTimeView] = useState<LeadTimeChartView>('approval');
  const [leadTimeGranularity, setLeadTimeGranularity] = useState<LeadTimeChartGranularity>('weekly');
  const trendLabels = {
    custo_por_pasta_aprovada: 'Custo por pasta aprovada',
    custo_por_pasta_enviada: 'Custo por pasta enviada',
    custo_por_venda: 'Custo por venda',
  };
  const currentMessageCost = summaryMessageCost(summary);
  const previousMessageCost = summaryMessageCost(previousSummary);
  const currentCpfReceivedCost = summaryCpfReceivedCost(summary);
  const previousCpfReceivedCost = summaryCpfReceivedCost(previousSummary);
  const currentCleanCpfCost = summaryCleanCpfCost(summary);
  const previousCleanCpfCost = summaryCleanCpfCost(previousSummary);
  const currentPurchaseCost = summaryPurchaseCost(summary);
  const previousPurchaseCost = summaryPurchaseCost(previousSummary);
  const leadWithCpfValue = summaryLeadWithCpfValue(summary);
  const totalInteractions = optionalNumber(summary.interacoes_totais);
  const additionalInteractions = numberValue(summary.interacoes_posteriores);
  const multiCampaignContacts = numberValue(summary.contatos_multicampanha);
  const multiCreativeContacts = numberValue(summary.contatos_multicriativo);
  const cpfReceivedAttributions = optionalNumber(summary.lead_com_cpf_atribuicoes);
  const duplicateCpfAttributions = numberValue(summary.lead_com_cpf_duplicidades_atribuicao);
  const multiAttributedCpfContacts = numberValue(summary.lead_com_cpf_multiatribuido);
  const recurringCpfReceivedFromPreviousPeriod = numberValue(summary.lead_com_cpf_recorrentes_periodo_anterior);
  const cleanCpfValue = summaryCleanCpfValue(summary);
  const hasEstimatedTimeMetrics = overview.data_kind === 'estimated';
  const usesLifetimeFirstTouch = overview.attribution_model === 'first_touch_lifetime';
  const conversionSteps: ConversionFlowStep[] = [
    {
      description: usesLifetimeFirstTouch ? 'Contatos atribuídos à primeira interação' : 'Início do funil',
      eventName: 'primeiro_contato',
      icon: 'ri-message-3-line',
      label: 'Primeiro Contato',
      note: usesLifetimeFirstTouch && totalInteractions !== null && additionalInteractions > 0
        ? `${formatNumber(additionalInteractions)} interações posteriores`
        : undefined,
      tone: 'primary',
      tooltip: usesLifetimeFirstTouch && totalInteractions !== null
        ? `Contatos únicos atribuídos à primeira interação da vida: ${formatNumber(summaryContactValue(summary))}. Interações registradas para esses contatos: ${formatNumber(totalInteractions)}. Interações posteriores que não alteram a origem: ${formatNumber(additionalInteractions)}. Contatos presentes em mais de uma campanha: ${formatNumber(multiCampaignContacts)}. Contatos presentes em mais de um criativo: ${formatNumber(multiCreativeContacts)}.`
        : 'Contatos únicos no início do funil.',
      value: summaryContactValue(summary),
    },
    {
      description: 'Contatos únicos com CPF recebido',
      eventName: 'dado_recebido',
      icon: 'ri-id-card-line',
      label: 'Lead com CPF',
      note: usesLifetimeFirstTouch && cpfReceivedAttributions !== null
        ? `${formatNumber(multiAttributedCpfContacts)} com interações em outros anúncios`
        : undefined,
      tone: 'primary',
      tooltip: usesLifetimeFirstTouch && cpfReceivedAttributions !== null
        ? `Contatos únicos com CPF recebido atribuídos ao primeiro toque: ${formatNumber(leadWithCpfValue)}. Interações vinculadas a anúncios: ${formatNumber(cpfReceivedAttributions)}. Aparições posteriores: ${formatNumber(duplicateCpfAttributions)}. Contatos que também interagiram com outros anúncios: ${formatNumber(multiAttributedCpfContacts)}. Também presentes no período anterior: ${formatNumber(recurringCpfReceivedFromPreviousPeriod)}. As interações posteriores são informativas e não entram novamente nas taxas do funil.`
        : 'Contatos únicos com CPF recebido.',
      value: leadWithCpfValue,
    },
    {
      description: 'Contatos com CPF válido',
      eventName: 'dado_positivo',
      icon: 'ri-shield-check-line',
      label: 'Lead com CPF Limpo',
      tone: 'primary',
      value: cleanCpfValue,
    },
    {
      description: 'Pastas enviadas no período',
      eventName: 'pasta_montada',
      icon: 'ri-folder-transfer-line',
      label: 'Pastas Enviadas',
      tone: 'primary',
      value: summary.pastas_enviadas,
    },
    {
      description: 'Pastas aprovadas no período',
      eventName: 'pasta_aprovada',
      icon: 'ri-checkbox-circle-line',
      label: 'Pastas Aprovadas',
      tone: 'primary',
      value: summary.pastas_aprovadas,
    },
    {
      description: 'Fechamentos atribuídos',
      eventName: 'venda',
      icon: 'ri-shopping-cart-line',
      label: 'Vendas',
      tone: 'primary',
      value: summary.purchases,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <MetricCard
          icon="ri-focus-3-line"
          label="Custo por Pasta Aprovada"
          value={formatMoney(summary.custo_por_pasta_aprovada)}
          description="KPI principal da operação"
          comparison={metricComparison(summary.custo_por_pasta_aprovada, previousSummary.custo_por_pasta_aprovada, 'decrease')}
          highlight
          tone="success"
        />
        <MetricCard
          icon="ri-wallet-3-line"
          label="Gasto Total"
          value={formatMoney(summary.gasto, 'R$ 0,00')}
          description="Investimento no período"
          comparison={metricComparison(summary.gasto, previousSummary.gasto, 'neutral')}
        />
      </div>

      <ConversionFlowSection steps={conversionSteps} />

      <OverviewMetricSection
        title="Eficiência (Custos)"
        description="Custos unitários das principais etapas de conversão."
        gridClassName="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5"
      >
        <MetricCard
          icon="ri-chat-1-line"
          label="Custo por Contato"
          value={formatMoney(currentMessageCost, 'Sem contato')}
          description="Gasto / primeiro contato"
          comparison={metricComparison(currentMessageCost, previousMessageCost, 'decrease')}
          tone="info"
        />
        <MetricCard
          icon="ri-id-card-line"
          label="Custo por CPF Recebido"
          value={formatMoney(currentCpfReceivedCost, 'Sem CPF recebido')}
          description="Gasto / CPFs recebidos"
          comparison={metricComparison(currentCpfReceivedCost, previousCpfReceivedCost, 'decrease')}
          tone="warning"
        />
        <MetricCard
          icon="ri-shield-check-line"
          label="Custo por CPF Limpo"
          value={formatMoney(currentCleanCpfCost, 'Sem CPF limpo')}
          description="Gasto / CPFs limpos"
          comparison={metricComparison(currentCleanCpfCost, previousCleanCpfCost, 'decrease')}
          tone="success"
        />
        <MetricCard
          icon="ri-coins-line"
          label="Custo / Pasta Enviada"
          value={formatMoney(summary.custo_por_pasta_enviada)}
          description="Gasto / pastas enviadas"
          comparison={metricComparison(summary.custo_por_pasta_enviada, previousSummary.custo_por_pasta_enviada, 'decrease')}
          tone="warning"
        />
        <MetricCard
          icon="ri-receipt-line"
          label="Custo por Venda"
          value={formatMoney(currentPurchaseCost, 'Sem venda')}
          description="Gasto / vendas"
          comparison={metricComparison(currentPurchaseCost, previousPurchaseCost, 'decrease')}
          tone="info"
        />
      </OverviewMetricSection>

      <OverviewMetricSection
        title="Velocidade (Tempos)"
        description="Tempo médio e mediano para avanço das pastas."
        gridClassName="grid grid-cols-1 gap-4 md:grid-cols-2"
      >
        <MetricCard
          icon="ri-timer-flash-line"
          label="Tempo Pasta Enviada"
          value={hasEstimatedTimeMetrics ? '-' : formatDurationPair(summary.tempo_medio_pasta_enviada_dias, summary.mediana_tempo_pasta_enviada_dias)}
          description={hasEstimatedTimeMetrics ? 'Indisponível para recorte estimado' : 'Média / mediana'}
          comparison={hasEstimatedTimeMetrics ? undefined : metricComparison(summary.tempo_medio_pasta_enviada_dias, previousSummary.tempo_medio_pasta_enviada_dias, 'decrease')}
          tooltip={hasEstimatedTimeMetrics ? estimatedTimeMetricTooltip : timeMetricTooltip(summary, 'sent')}
          tone="warning"
        />
        <MetricCard
          icon="ri-timer-line"
          label="Tempo até Pasta Aprovada"
          value={hasEstimatedTimeMetrics ? '-' : formatDurationPair(summary.tempo_medio_pasta_aprovada_dias, summary.mediana_tempo_pasta_aprovada_dias)}
          description={hasEstimatedTimeMetrics ? 'Indisponível para recorte estimado' : 'Média / mediana'}
          comparison={hasEstimatedTimeMetrics ? undefined : metricComparison(summary.tempo_medio_pasta_aprovada_dias, previousSummary.tempo_medio_pasta_aprovada_dias, 'decrease')}
          tooltip={hasEstimatedTimeMetrics ? estimatedTimeMetricTooltip : timeMetricTooltip(summary, 'approved')}
          tone="success"
        />
      </OverviewMetricSection>

      <div className="grid grid-cols-1 gap-4">
        <section className={habitaSurfaceClass}>
          <HabitaSectionHeader title={`Evolução - gasto x ${trendLabels[trendMetric].toLowerCase()}`} titleClassName="text-base">
            <HabitaSelect
              value={trendMetric}
              onChange={(event) => setTrendMetric(event.target.value as typeof trendMetric)}
              options={[
                { label: 'Custo por pasta aprovada', value: 'custo_por_pasta_aprovada' },
                { label: 'Custo por venda', value: 'custo_por_venda' },
                { label: 'Custo por pasta enviada', value: 'custo_por_pasta_enviada' },
              ]}
              className="w-56"
              aria-label="Métrica da evolução"
            />
          </HabitaSectionHeader>
          <div className="space-y-3 p-5">
            <MarketingEvolutionChart
              data={overview.daily}
              metric={trendMetric}
              metricLabel={trendLabels[trendMetric]}
            />
          </div>
        </section>
        <section className={habitaSurfaceClass}>
          <HabitaSectionHeader title={`Tendência - ${leadTimeChartConfig[leadTimeView].title} (dias)`} titleClassName="text-base">
            <div className="flex flex-wrap items-center gap-2">
              <HabitaSelect
                  value={leadTimeGranularity}
                  onChange={(event) => setLeadTimeGranularity(event.target.value as LeadTimeChartGranularity)}
                  options={[
                    { label: 'Diário', value: 'daily' },
                    { label: 'Semanal', value: 'weekly' },
                    { label: 'Mensal', value: 'monthly' },
                  ]}
                  className="w-36"
                  aria-label="Granularidade do tempo de conversão"
              />
              <HabitaSelect
                  value={leadTimeView}
                  onChange={(event) => setLeadTimeView(event.target.value as LeadTimeChartView)}
                  options={[
                    { label: 'Tempo até Pasta Aprovada', value: 'approval' },
                    { label: 'Tempo até Conversão do Lead', value: 'leadConversion' },
                  ]}
                  className="w-60"
                  aria-label="Visão do tempo de conversão"
              />
            </div>
          </HabitaSectionHeader>
          <div className="p-5">
            <LeadTimeEvolutionChart overview={overview} view={leadTimeView} granularity={leadTimeGranularity} />
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PerformanceRankingList
          onRequestRanking={onRequestRanking}
          overview={overview}
          rankingLoadingLevel={rankingLoadingLevel}
        />
        <RankingList
          costKey="custo_estimado_por_pasta_aprovada"
          emptyLabel="Nenhuma região encontrada."
          labelKey="region"
          rows={overview.regions}
          subtitle={(row) => `${formatNumber(row.pastas_aprovadas_estimadas, 2)} aprovadas estimadas · ${formatMoney(row.gasto, 'R$ 0,00')}`}
          title="Ranking de regiões estimadas"
        />
      </div>
    </div>
  );
}

function CreativePreview({ className = 'h-12 w-12', row }: { className?: string; row: MarketingRow }) {
  const url = String(row.thumbnail_url ?? '').trim();
  const title = firstText(row, ['creative_title', 'ad_name', 'creative_id']);

  if (url) {
    return (
      <img
        src={url}
        alt={title}
        className={classNames('shrink-0 rounded-md border border-border-subtle object-cover', className)}
        loading="lazy"
      />
    );
  }

  return (
    <div className={classNames('relative flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-muted text-textSecondary', className)}>
      <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--muted)),hsl(var(--accent)))]" />
      <div className="relative flex max-w-full flex-col items-center justify-center gap-1 px-2 text-center">
        <i className="ri-image-line text-lg" aria-hidden />
        {className.includes('h-32') || className.includes('h-36') ? (
          <span className="line-clamp-2 text-[10px] font-medium leading-tight">Imagem não disponível</span>
        ) : null}
      </div>
      {className.includes('h-32') || className.includes('h-36') ? (
        <span className="absolute bottom-1 left-1.5 right-1.5 truncate text-[10px] font-medium text-textSecondary">{title}</span>
      ) : null}
    </div>
  );
}

const sumKeys = [
  'clicks',
  'contatos',
  'contatos_estimados',
  'contatos_multicampanha',
  'contatos_multicriativo',
  'gasto',
  'first_messages',
  'first_messages_estimadas',
  'interacoes_posteriores',
  'interacoes_totais',
  'inline_link_clicks',
  'impressions',
  'lead_com_cpf',
  'lead_com_cpf_atribuicoes',
  'lead_com_cpf_duplicidades_atribuicao',
  'lead_com_cpf_duplicados_periodo',
  'lead_com_cpf_estimados',
  'lead_com_cpf_limpo',
  'lead_com_cpf_limpo_estimados',
  'lead_com_cpf_multiatribuido',
  'lead_com_cpf_ocorrencias',
  'lead_com_cpf_recorrentes_periodo_anterior',
  'agendamentos_presenciais',
  'pastas_aprovadas',
  'pastas_aprovadas_estimadas',
  'pastas_enviadas',
  'pastas_enviadas_estimadas',
  'purchases',
  'unique_clicks',
  'unique_inline_link_clicks',
];

const scopedAggregateTotalKeys = [
  'clicks',
  'contatos',
  'contatos_multicampanha',
  'contatos_multicriativo',
  'gasto',
  'first_messages',
  'interacoes_posteriores',
  'interacoes_totais',
  'inline_link_clicks',
  'impressions',
  'lead_com_cpf',
  'lead_com_cpf_atribuicoes',
  'lead_com_cpf_duplicidades_atribuicao',
  'lead_com_cpf_duplicados_periodo',
  'lead_com_cpf_limpo',
  'lead_com_cpf_multiatribuido',
  'lead_com_cpf_ocorrencias',
  'lead_com_cpf_recorrentes_periodo_anterior',
  'agendamentos_presenciais',
  'lead_submitted',
  'pastas_aprovadas',
  'pastas_enviadas',
  'purchases',
  'unique_clicks',
  'unique_inline_link_clicks',
];

function applyScopedAggregateTotals(aggregate: MarketingRow, rows: MarketingRow[], scope: CampaignAggregationScope) {
  if (scope === 'creative') return;

  const prefix = scope === 'campaign' ? 'campaign_total' : 'adset_total';
  const totalsSource = rows.find((row) => (
    scopedAggregateTotalKeys.some((key) => optionalNumber(row[`${prefix}_${key}`]) !== null)
  ));

  if (!totalsSource) return;

  scopedAggregateTotalKeys.forEach((key) => {
    const total = optionalNumber(totalsSource[`${prefix}_${key}`]);
    if (total !== null) {
      aggregate[key] = total;
    }
  });
}

function aggregateRows(rows: MarketingRow[], scope: CampaignAggregationScope = 'creative'): MarketingRow {
  const aggregate: MarketingRow = { ...(rows[0] ?? {}) };
  sumKeys.forEach((key) => {
    aggregate[key] = rows.reduce((total, row) => total + numberValue(row[key]), 0);
  });
  applyScopedAggregateTotals(aggregate, rows, scope);
  aggregate.ctr = costPer(aggregate.clicks, aggregate.impressions) === null
    ? null
    : Number((numberValue(aggregate.clicks) / numberValue(aggregate.impressions) * 100).toFixed(2));
  aggregate.custo_por_pasta_enviada = costPer(aggregate.gasto, aggregate.pastas_enviadas);
  aggregate.custo_estimado_por_pasta_enviada = costPer(aggregate.gasto, aggregate.pastas_enviadas_estimadas);
  aggregate.custo_por_pasta_aprovada = costPer(aggregate.gasto, aggregate.pastas_aprovadas);
  aggregate.custo_estimado_por_pasta_aprovada = costPer(aggregate.gasto, aggregate.pastas_aprovadas_estimadas);
  aggregate.custo_por_contato = costPer(aggregate.gasto, contactValue(aggregate));
  aggregate.custo_por_cpf_recebido = costPer(aggregate.gasto, aggregate.lead_com_cpf);
  aggregate.custo_por_cpf_limpo = costPer(aggregate.gasto, aggregate.lead_com_cpf_limpo);
  aggregate.custo_por_venda = costPer(aggregate.gasto, purchasesValue(aggregate));
  applyTimeMetrics(aggregate, rows, scope);
  return aggregate;
}

interface CampaignAdsetGroup {
  creatives: MarketingRow[];
  id: string;
  row: MarketingRow;
}

interface CampaignGroup {
  adsets: CampaignAdsetGroup[];
  campaign: MarketingRow;
  id: string;
}

interface MarketingStatusCounts {
  active: number;
  inactive: number;
  unknown: number;
}

interface MarketingDetailedStatusCount {
  count: number;
  key: string;
  row: MarketingRow;
}

function compareMarketingStatus(left: MarketingRow, right: MarketingRow, level: MarketingEntityLevel): number {
  const statusOrder: Record<MarketingEntityStatus, number> = {
    active: 0,
    inactive: 1,
    unknown: 2,
  };
  return statusOrder[marketingEntityStatus(left, level)] - statusOrder[marketingEntityStatus(right, level)];
}

function countMarketingStatuses(rows: MarketingRow[], level: MarketingEntityLevel): MarketingStatusCounts {
  return rows.reduce<MarketingStatusCounts>((counts, row) => {
    counts[marketingEntityStatus(row, level)] += 1;
    return counts;
  }, { active: 0, inactive: 0, unknown: 0 });
}

function countMarketingDetailedStatuses(
  rows: MarketingRow[],
  level: MarketingEntityLevel
): MarketingDetailedStatusCount[] {
  const groups = new Map<string, MarketingDetailedStatusCount>();

  rows.forEach((row) => {
    const key = marketingEntityStatusCountLabel(row, level, 1);
    const current = groups.get(key);
    groups.set(key, current ? { ...current, count: current.count + 1 } : { count: 1, key, row });
  });

  return Array.from(groups.values()).sort((left, right) => {
    const statusComparison = compareMarketingStatus(left.row, right.row, level);
    if (statusComparison !== 0) return statusComparison;
    const leftPaused = left.key === 'pausado' ? 0 : 1;
    const rightPaused = right.key === 'pausado' ? 0 : 1;
    if (leftPaused !== rightPaused) return leftPaused - rightPaused;
    return marketingEntityStatusLabel(left.row, level).localeCompare(marketingEntityStatusLabel(right.row, level), 'pt-BR');
  });
}

function CreativeStatusBreakdown({ rows }: { rows: MarketingRow[] }) {
  const statusCounts = countMarketingDetailedStatuses(rows, 'creative');

  return (
    <div
      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-textSecondary"
      aria-label={`Distribuição de ${rows.length} criativo(s) por status`}
    >
      <span>{rows.length} criativo(s)</span>
      {statusCounts.map((statusCount) => (
        <span key={statusCount.key} className="inline-flex items-center gap-1 whitespace-nowrap">
          <span
            className={classNames('h-1.5 w-1.5 rounded-full', marketingStatusDotTone(statusCount.row, 'creative'))}
            aria-hidden
          />
          {statusCount.count} {marketingEntityStatusCountLabel(statusCount.row, 'creative', statusCount.count)}
        </span>
      ))}
    </div>
  );
}

function MarketingStatusCountsSummary({
  counts,
  label,
  level,
}: {
  counts: MarketingStatusCounts;
  label: string;
  level: 'adset' | 'campaign';
}) {
  const feminine = level === 'campaign';

  return (
    <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-border-subtle bg-muted/40 px-2 py-1 font-instrument text-[11px]">
      <span className="font-semibold text-textPrimary">{label}</span>
      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-200">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
        {counts.active} {feminine ? 'ativas' : 'ativos'}
      </span>
      <span className="inline-flex items-center gap-1 text-textSecondary">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-500" aria-hidden />
        {counts.inactive} {feminine ? 'inativas' : 'inativos'}
      </span>
      {counts.unknown > 0 && (
        <span className="inline-flex items-center gap-1 text-amber-800 dark:text-amber-100">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
          {counts.unknown} sem status
        </span>
      )}
    </div>
  );
}

function hasCreativeIdentity(row: MarketingRow): boolean {
  return firstText(row, ['ad_id', 'creative_id', 'ad_name', 'creative_name']) !== '-';
}

function matchesAnyRowText(row: MarketingRow, keys: string[], target: string): boolean {
  if (target === '-') return true;
  return keys.some((key) => rowText(row, key) === target);
}

function aggregateCreativeRows(rows: MarketingRow[]): MarketingRow[] {
  const map = new Map<string, MarketingRow[]>();

  rows.forEach((row, index) => {
    const key = firstText(row, ['ad_id', 'creative_id', 'ad_name', 'creative_name'], `creative-${index}`);
    map.set(key, [...(map.get(key) ?? []), row]);
  });

  return Array.from(map.values())
    .map((creativeRows) => aggregateRows(creativeRows, 'creative'))
    .sort((left, right) => compareMarketingStatus(left, right, 'creative'));
}

function groupCampaignRows(rows: MarketingRow[], adsRows: MarketingRow[] = []): CampaignGroup[] {
  const map = new Map<string, MarketingRow[]>();
  rows.forEach((row, index) => {
    const key = firstText(row, ['campaign_id', 'campaign_name'], `campaign-${index}`);
    map.set(key, [...(map.get(key) ?? []), row]);
  });

  return Array.from(map.entries()).map(([id, campaignRows]) => {
    const adsetMap = new Map<string, MarketingRow[]>();
    campaignRows.forEach((row, index) => {
      const key = firstText(row, ['adset_id', 'adset_name'], `adset-${index}`);
      adsetMap.set(key, [...(adsetMap.get(key) ?? []), row]);
    });

    const adsets = Array.from(adsetMap.entries()).map(([adsetId, adsetRows]) => {
      const row = aggregateRows(adsetRows, 'adset');
      const campaignValue = firstText(row, ['campaign_id', 'campaign_name']);
      const adsetValue = firstText(row, ['adset_id', 'adset_name']);
      const creativeSource = adsetRows.filter(hasCreativeIdentity);
      const fallbackCreatives = creativeSource.length > 0
        ? []
        : adsRows.filter((adRow) => (
          hasCreativeIdentity(adRow)
          && matchesAnyRowText(adRow, ['campaign_id', 'campaign_name'], campaignValue)
          && matchesAnyRowText(adRow, ['adset_id', 'adset_name'], adsetValue)
        ));
      const creatives = aggregateCreativeRows(creativeSource.length > 0 ? creativeSource : fallbackCreatives);

      return {
        creatives,
        id: `${id}-${adsetId}`,
        row,
      };
    }).sort((left, right) => compareMarketingStatus(left.row, right.row, 'adset'));

    return {
      adsets,
      campaign: aggregateRows(campaignRows, 'campaign'),
      id,
    };
  }).sort((left, right) => compareMarketingStatus(left.campaign, right.campaign, 'campaign'));
}

type CampaignInterestRegionMetricMap = Map<string, Map<CampaignInterestRegionColumnKey, number>>;

interface CampaignInterestRegionIndex {
  adset: CampaignInterestRegionMetricMap;
  campaign: CampaignInterestRegionMetricMap;
  creative: CampaignInterestRegionMetricMap;
}

function campaignInterestRegionEntityKey(
  row: MarketingRow,
  scope: CampaignAggregationScope,
): string {
  const campaign = firstText(row, ['campaign_id', 'campaign_name'], 'sem-campanha');
  if (scope === 'campaign') return campaign;

  const adset = firstText(row, ['adset_id', 'adset_name'], 'sem-adset');
  if (scope === 'adset') return `${campaign}::${adset}`;

  const creative = firstText(row, ['ad_id', 'creative_id', 'ad_name', 'creative_name'], 'sem-criativo');
  return `${campaign}::${adset}::${creative}`;
}

function normalizedCampaignInterestCity(value: unknown): string {
  const city = String(value ?? '').trim();
  if (city === 'Sto. Antônio Descoberto, GO') return 'Santo Antônio do Descoberto, GO';
  return city;
}

function addCampaignInterestRegionValue(
  target: CampaignInterestRegionMetricMap,
  entityKey: string,
  columnKey: CampaignInterestRegionColumnKey,
  value: number,
) {
  const metrics = target.get(entityKey) ?? new Map<CampaignInterestRegionColumnKey, number>();
  metrics.set(columnKey, (metrics.get(columnKey) ?? 0) + value);
  target.set(entityKey, metrics);
}

function buildCampaignInterestRegionIndex(
  response: MarketingInterestRegionsResponse,
): CampaignInterestRegionIndex {
  const index: CampaignInterestRegionIndex = {
    adset: new Map(),
    campaign: new Map(),
    creative: new Map(),
  };

  response.rows.forEach((row) => {
    const value = numberValue(row.leads_interesse);
    if (value <= 0) return;

    const city = normalizedCampaignInterestCity(row.cidade_interesse);
    const isOther = row.outra_localidade === true;
    const isMissing = row.regiao_nao_informada === true
      || city === 'Região não informada'
      || (!city && !isOther);
    const matchingCityColumn = campaignInterestRegionColumns.find((column) => column.city === city);

    (['campaign', 'adset', 'creative'] as const).forEach((scope) => {
      const entityKey = campaignInterestRegionEntityKey(row, scope);
      if (!isMissing) {
        addCampaignInterestRegionValue(index[scope], entityKey, 'interestRegionsAll', value);
      }
      if (isOther) {
        addCampaignInterestRegionValue(index[scope], entityKey, 'interestRegionOther', value);
      }
      if (matchingCityColumn) {
        addCampaignInterestRegionValue(index[scope], entityKey, matchingCityColumn.key, value);
      }
    });
  });

  return index;
}

function campaignInterestRegionValue(
  index: CampaignInterestRegionIndex,
  row: MarketingRow,
  scope: CampaignAggregationScope,
  columnKey: CampaignInterestRegionColumnKey,
): number {
  return index[scope].get(campaignInterestRegionEntityKey(row, scope))?.get(columnKey) ?? 0;
}

const campaignReconciliationMetrics: Array<{
  rowKey: string;
  summaryKey: keyof MarketingSummary;
}> = [
  { rowKey: 'contatos', summaryKey: 'contatos' },
  { rowKey: 'first_messages', summaryKey: 'first_messages' },
  { rowKey: 'lead_com_cpf', summaryKey: 'lead_com_cpf' },
  { rowKey: 'lead_com_cpf_limpo', summaryKey: 'lead_com_cpf_limpo' },
  { rowKey: 'pastas_enviadas', summaryKey: 'pastas_enviadas' },
  { rowKey: 'pastas_aprovadas', summaryKey: 'pastas_aprovadas' },
  { rowKey: 'purchases', summaryKey: 'purchases' },
];

function buildUnattributedCampaignGroup(
  groups: CampaignGroup[],
  summary: MarketingSummary
): CampaignGroup | null {
  const unattributedRow: MarketingRow = {
    campaign_id: 'sem-campanha-identificada',
    campaign_name: 'Sem campanha identificada',
    is_unattributed: true,
  };
  let hasUnattributedMetrics = false;

  campaignReconciliationMetrics.forEach(({ rowKey, summaryKey }) => {
    const summaryTotal = optionalNumber(summary[summaryKey]);
    if (summaryTotal === null) return;

    const attributedTotal = groups.reduce(
      (total, group) => total + numberValue(group.campaign[rowKey]),
      0
    );
    const difference = Math.max(0, summaryTotal - attributedTotal);
    const normalizedDifference = Number(difference.toFixed(2));

    unattributedRow[rowKey] = normalizedDifference;
    if (normalizedDifference > 0) hasUnattributedMetrics = true;
  });

  if (!hasUnattributedMetrics) return null;

  unattributedRow.lead_submitted = unattributedRow.lead_com_cpf;

  return {
    adsets: [],
    campaign: unattributedRow,
    id: 'sem-campanha-identificada',
  };
}

function ConversionPercentBadge({
  funnelBase,
  previousBase,
  quantity,
  showPreviousConversion = false,
}: {
  funnelBase: unknown;
  previousBase?: unknown;
  quantity: unknown;
  showPreviousConversion?: boolean;
}) {
  const funnelConversion = conversionPercent(quantity, funnelBase);
  const previousConversion = showPreviousConversion ? conversionPercent(quantity, previousBase) : null;
  const quantityLabel = formatNumber(quantity);
  const funnelLabel = formatPercent(funnelConversion);
  const previousLabel = formatPercent(previousConversion);
  const tooltip = [
    `Quantidade: ${quantityLabel}`,
    `Conversão do funil: ${funnelLabel}`,
    ...(showPreviousConversion ? [`Conversão da etapa anterior: ${previousLabel}`] : []),
  ].join('\n');

  return (
    <span
      className="cursor-help whitespace-nowrap rounded-sm px-1 text-[11px] font-medium text-textSecondary underline decoration-dotted underline-offset-2"
      title={tooltip}
    >
      ({funnelLabel})
    </span>
  );
}

function FunnelMetricCell({
  funnelBase,
  multiAttributedCount,
  previousBase,
  quantity,
  showPreviousConversion = false,
}: {
  funnelBase: unknown;
  multiAttributedCount?: unknown;
  previousBase?: unknown;
  quantity: unknown;
  showPreviousConversion?: boolean;
}) {
  const multiAttributed = optionalNumber(multiAttributedCount);

  return (
    <td className="px-4 py-3 text-right font-mono">
      <div className="flex flex-col items-end gap-0.5">
        <span className="inline-flex items-center justify-end gap-1">
          <span>{formatNumber(quantity)}</span>
          <ConversionPercentBadge
            funnelBase={funnelBase}
            previousBase={previousBase}
            quantity={quantity}
            showPreviousConversion={showPreviousConversion}
          />
        </span>
        {multiAttributed !== null && multiAttributed > 0 ? (
          <span
            className="cursor-help whitespace-nowrap font-instrument text-[10px] font-medium text-amber-700 dark:text-amber-300"
            title={`${formatNumber(multiAttributed)} contato(s) desta linha também interagiram com outros anúncios. A conversão permanece atribuída somente ao anúncio da primeira interação da vida.`}
          >
            {formatNumber(multiAttributed)} em outros anúncios
          </span>
        ) : null}
      </div>
    </td>
  );
}

function FirstTouchContactCell({ row }: { row: MarketingRow }) {
  const contacts = contactValue(row);
  const additionalInteractions = numberValue(row.interacoes_posteriores);
  const multiCampaignContacts = numberValue(row.contatos_multicampanha);
  const multiCreativeContacts = numberValue(row.contatos_multicriativo);
  const showDiagnostics = additionalInteractions > 0
    || multiCampaignContacts > 0
    || multiCreativeContacts > 0;
  const tooltip = [
    `Contatos atribuídos à primeira interação: ${formatNumber(contacts)}`,
    `Interações posteriores: ${formatNumber(additionalInteractions)}`,
    `Contatos em mais de uma campanha: ${formatNumber(multiCampaignContacts)}`,
    `Contatos em mais de um criativo: ${formatNumber(multiCreativeContacts)}`,
    'Interações posteriores são exibidas para auditoria e não duplicam as conversões.',
  ].join('\n');

  return (
    <td className="px-4 py-3 text-right font-mono">
      <div className="flex flex-col items-end gap-0.5">
        <span>{formatNumber(contacts)}</span>
        {showDiagnostics ? (
          <span
            className="cursor-help whitespace-nowrap font-instrument text-[10px] font-medium text-amber-700 dark:text-amber-300"
            title={tooltip}
          >
            {formatNumber(additionalInteractions)} interações posteriores
          </span>
        ) : null}
      </div>
    </td>
  );
}

function TimeMetricCell({
  row,
  stage,
  valueKey,
  valueLabel,
}: {
  row: MarketingRow;
  stage: 'approved' | 'sent';
  valueKey: 'mediana_tempo_pasta_aprovada_dias' | 'mediana_tempo_pasta_enviada_dias' | 'tempo_medio_pasta_aprovada_dias' | 'tempo_medio_pasta_enviada_dias';
  valueLabel: string;
}) {
  const minKey = stage === 'sent' ? 'menor_tempo_pasta_enviada_dias' : 'menor_tempo_pasta_aprovada_dias';
  const maxKey = stage === 'sent' ? 'maior_tempo_pasta_enviada_dias' : 'maior_tempo_pasta_aprovada_dias';
  const value = row[valueKey];
  const minValueLabel = formatDurationDays(row[minKey]);
  const maxValueLabel = formatDurationDays(row[maxKey]);
  const tooltip = [
    `${valueLabel}: ${formatDurationDays(value)}`,
    `Menor tempo observado no período: ${minValueLabel}`,
    `Maior tempo observado no período: ${maxValueLabel}`,
  ].join('\n');

  return (
    <td className="px-4 py-3 text-right font-mono">
      <span
        className="cursor-help whitespace-nowrap underline decoration-dotted underline-offset-2"
        title={tooltip}
      >
        {formatDurationDays(value)}
      </span>
    </td>
  );
}

function CampaignsAdsetsPrototypeTab({
  adsRows,
  attributionModel,
  interestRegions,
  interestRegionsLoaded,
  onRequestOtherLocalities,
  rows,
  summary,
}: {
  adsRows: MarketingRow[];
  attributionModel?: 'first_touch_lifetime';
  interestRegions: MarketingInterestRegionsResponse;
  interestRegionsLoaded: boolean;
  onRequestOtherLocalities: (
    row: MarketingRow,
    scope: CampaignAggregationScope,
  ) => Promise<MarketingOtherInterestLocality[]>;
  rows: MarketingRow[];
  summary: MarketingSummary;
}) {
  const usesLifetimeFirstTouch = attributionModel === 'first_touch_lifetime';
  const groups = useMemo(() => {
    const campaignGroups = groupCampaignRows(rows, adsRows);
    const unattributedGroup = buildUnattributedCampaignGroup(campaignGroups, summary);
    return unattributedGroup ? [...campaignGroups, unattributedGroup] : campaignGroups;
  }, [adsRows, rows, summary]);
  const interestRegionIndex = useMemo(
    () => buildCampaignInterestRegionIndex(interestRegions),
    [interestRegions],
  );
  const campaignStatusCounts = useMemo(
    () => countMarketingStatuses(
      groups
        .filter((group) => group.campaign.is_unattributed !== true)
        .map((group) => group.campaign),
      'campaign'
    ),
    [groups]
  );
  const adsetStatusCounts = useMemo(
    () => countMarketingStatuses(groups.flatMap((group) => group.adsets.map((adset) => adset.row)), 'adset'),
    [groups]
  );
  const [openCampaigns, setOpenCampaigns] = useState<Record<string, boolean>>({});
  const [openAdsets, setOpenAdsets] = useState<Record<string, boolean>>({});
  const [columnOrderKeys, setColumnOrderKeys] = useState<CampaignColumnKey[]>(defaultCampaignColumnOrderKeys);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<CampaignColumnKey[]>(defaultCampaignColumnKeys);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [columnMenuPosition, setColumnMenuPosition] = useState<CSSProperties | null>(null);
  const [otherLocalitiesDialog, setOtherLocalitiesDialog] = useState<{
    contextLabel?: string;
    error: string | null;
    isLoading: boolean;
    isOpen: boolean;
    items: MarketingOtherInterestLocality[];
  }>({ error: null, isLoading: false, isOpen: false, items: [] });
  const [draggingColumnKey, setDraggingColumnKey] = useState<CampaignColumnKey | null>(null);
  const [columnDropTarget, setColumnDropTarget] = useState<{ key: CampaignColumnKey; placement: 'after' | 'before' } | null>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const floatingScrollRef = useRef<HTMLDivElement>(null);
  const columnMenuRef = useRef<HTMLDivElement>(null);
  const columnPopoverRef = useRef<HTMLDivElement>(null);
  const otherLocalitiesRequestRef = useRef(0);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const expandableCampaignIds = useMemo(() => groups.filter((group) => group.adsets.length > 0).map((group) => group.id), [groups]);
  const expandableAdsetIds = useMemo(() => groups.flatMap((group) => group.adsets.filter((adset) => adset.creatives.length > 0).map((adset) => adset.id)), [groups]);
  const visibleCampaignColumns = useMemo(
    () => columnOrderKeys
      .map((key) => campaignColumnByKey.get(key))
      .filter((column): column is { key: CampaignColumnKey; label: string } => Boolean(column))
      .filter((column) => visibleColumnKeys.includes(column.key)),
    [columnOrderKeys, visibleColumnKeys]
  );
  const allExpanded = expandableCampaignIds.length > 0
    && expandableCampaignIds.every((id) => openCampaigns[id])
    && expandableAdsetIds.every((id) => openAdsets[id]);

  useEffect(() => {
    setOpenCampaigns({});
    setOpenAdsets({});
  }, [groups]);

  useEffect(() => {
    otherLocalitiesRequestRef.current += 1;
    setOtherLocalitiesDialog((current) => (
      current.isOpen ? { ...current, isOpen: false } : current
    ));

    return () => {
      otherLocalitiesRequestRef.current += 1;
    };
  }, [onRequestOtherLocalities]);

  useEffect(() => {
    const tableScroller = tableScrollRef.current;
    if (!tableScroller) return undefined;

    function updateScrollWidth() {
      const currentScroller = tableScrollRef.current;
      if (!currentScroller) return;
      setTableScrollWidth(currentScroller.scrollWidth);
      setHasHorizontalOverflow(currentScroller.scrollWidth > currentScroller.clientWidth + 1);
    }

    updateScrollWidth();
    window.addEventListener('resize', updateScrollWidth);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', updateScrollWidth);
    }

    const observer = new ResizeObserver(updateScrollWidth);
    observer.observe(tableScroller);
    if (tableScroller.firstElementChild) observer.observe(tableScroller.firstElementChild);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScrollWidth);
    };
  }, [columnOrderKeys, groups, visibleCampaignColumns.length]);

  useEffect(() => {
    if (!isColumnMenuOpen) return undefined;

    function updateColumnMenuPosition() {
      const trigger = columnMenuRef.current;
      if (!trigger) return;

      const viewportPadding = 12;
      const gap = 8;
      const rect = trigger.getBoundingClientRect();
      const width = Math.max(0, Math.min(320, window.innerWidth - viewportPadding * 2));
      const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
      const spaceAbove = rect.top - gap - viewportPadding;
      const openAbove = spaceBelow < 280 && spaceAbove > spaceBelow;
      const availableHeight = Math.max(0, openAbove ? spaceAbove : spaceBelow);
      const left = Math.min(
        Math.max(viewportPadding, rect.right - width),
        Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
      );

      setColumnMenuPosition({
        bottom: openAbove ? window.innerHeight - rect.top + gap : undefined,
        left,
        maxHeight: Math.min(480, availableHeight),
        top: openAbove ? undefined : rect.bottom + gap,
        width,
      });
    }

    updateColumnMenuPosition();

    function closeColumnMenuOnOutsideInteraction(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (columnMenuRef.current?.contains(target)) return;
      if (columnPopoverRef.current?.contains(target)) return;
      setIsColumnMenuOpen(false);
    }

    function closeColumnMenuOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsColumnMenuOpen(false);
    }

    document.addEventListener('mousedown', closeColumnMenuOnOutsideInteraction);
    document.addEventListener('keydown', closeColumnMenuOnEscape);
    window.addEventListener('resize', updateColumnMenuPosition);
    window.addEventListener('scroll', updateColumnMenuPosition, true);

    return () => {
      document.removeEventListener('mousedown', closeColumnMenuOnOutsideInteraction);
      document.removeEventListener('keydown', closeColumnMenuOnEscape);
      window.removeEventListener('resize', updateColumnMenuPosition);
      window.removeEventListener('scroll', updateColumnMenuPosition, true);
    };
  }, [isColumnMenuOpen]);

  function syncTableScroll(source: 'table' | 'floating') {
    const tableScroller = tableScrollRef.current;
    const floatingScroller = floatingScrollRef.current;
    if (!tableScroller || !floatingScroller) return;

    const origin = source === 'table' ? tableScroller : floatingScroller;
    const target = source === 'table' ? floatingScroller : tableScroller;
    if (Math.abs(target.scrollLeft - origin.scrollLeft) > 1) {
      target.scrollLeft = origin.scrollLeft;
    }
  }

  function toggleAllRows() {
    if (allExpanded) {
      setOpenCampaigns({});
      setOpenAdsets({});
      return;
    }

    setOpenCampaigns(Object.fromEntries(expandableCampaignIds.map((id) => [id, true])));
    setOpenAdsets(Object.fromEntries(expandableAdsetIds.map((id) => [id, true])));
  }

  function toggleColumnVisibility(key: CampaignColumnKey) {
    setVisibleColumnKeys((current) => {
      if (current.includes(key)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== key);
      }

      return [...current, key];
    });
  }

  function moveCampaignColumn(draggedKey: CampaignColumnKey, targetKey: CampaignColumnKey, placement: 'after' | 'before') {
    if (draggedKey === targetKey) return;

    setColumnOrderKeys((current) => {
      if (!current.includes(draggedKey) || !current.includes(targetKey)) return current;

      const next = current.filter((key) => key !== draggedKey);
      const targetIndex = next.indexOf(targetKey);
      if (targetIndex < 0) return current;

      next.splice(placement === 'after' ? targetIndex + 1 : targetIndex, 0, draggedKey);
      return next;
    });
  }

  function startColumnDrag(event: DragEvent<HTMLDivElement>, key: CampaignColumnKey) {
    setDraggingColumnKey(key);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', key);
  }

  function updateColumnDropTarget(event: DragEvent<HTMLDivElement>, key: CampaignColumnKey) {
    event.preventDefault();
    const placement = event.clientY > event.currentTarget.getBoundingClientRect().top + event.currentTarget.offsetHeight / 2
      ? 'after'
      : 'before';
    event.dataTransfer.dropEffect = 'move';
    setColumnDropTarget({ key, placement });
  }

  function dropColumn(event: DragEvent<HTMLDivElement>, key: CampaignColumnKey) {
    event.preventDefault();
    const draggedKey = draggingColumnKey ?? event.dataTransfer.getData('text/plain') as CampaignColumnKey;
    const placement = event.clientY > event.currentTarget.getBoundingClientRect().top + event.currentTarget.offsetHeight / 2
      ? 'after'
      : 'before';
    moveCampaignColumn(draggedKey, key, placement);
    setDraggingColumnKey(null);
    setColumnDropTarget(null);
  }

  function endColumnDrag() {
    setDraggingColumnKey(null);
    setColumnDropTarget(null);
  }

  function otherLocalitiesContextLabel(row: MarketingRow, scope: CampaignAggregationScope): string {
    if (scope === 'campaign') {
      return `Campanha: ${firstText(row, ['campaign_name', 'campaign_id'])}`;
    }
    if (scope === 'adset') {
      return `Adset: ${firstText(row, ['adset_name', 'adset_id'])}`;
    }
    return `Criativo: ${creativeName(row)}`;
  }

  function openAllOtherLocalities() {
    otherLocalitiesRequestRef.current += 1;
    setOtherLocalitiesDialog({
      contextLabel: 'todos os itens exibidos',
      error: null,
      isLoading: false,
      isOpen: true,
      items: interestRegions.other_localities ?? [],
    });
  }

  async function openScopedOtherLocalities(row: MarketingRow, scope: CampaignAggregationScope) {
    const requestId = otherLocalitiesRequestRef.current + 1;
    otherLocalitiesRequestRef.current = requestId;
    setOtherLocalitiesDialog({
      contextLabel: otherLocalitiesContextLabel(row, scope),
      error: null,
      isLoading: true,
      isOpen: true,
      items: [],
    });

    try {
      const items = await onRequestOtherLocalities(row, scope);
      if (otherLocalitiesRequestRef.current !== requestId) return;
      setOtherLocalitiesDialog((current) => ({
        ...current,
        isLoading: false,
        items,
      }));
    } catch (requestError) {
      console.error('Erro ao carregar outras localidades da linha:', requestError);
      if (otherLocalitiesRequestRef.current !== requestId) return;
      setOtherLocalitiesDialog((current) => ({
        ...current,
        error: 'Não foi possível carregar as localidades deste item. Tente novamente.',
        isLoading: false,
      }));
    }
  }

  function closeOtherLocalitiesDialog() {
    otherLocalitiesRequestRef.current += 1;
    setOtherLocalitiesDialog((current) => ({ ...current, isOpen: false }));
  }

  if (rows.length === 0) {
    return <HabitaEmptyState icon="ri-stack-line" title="Nenhuma campanha encontrada" description="Ajuste os filtros ou tente outro período." />;
  }

  function renderMetricCell(
    row: MarketingRow,
    key: CampaignColumnKey,
    scope: CampaignAggregationScope,
  ): ReactNode {
    const interestRegionColumn = campaignInterestRegionColumnByKey.get(key as CampaignInterestRegionColumnKey);
    if (interestRegionColumn) {
      const value = interestRegionsLoaded
        ? campaignInterestRegionValue(interestRegionIndex, row, scope, interestRegionColumn.key)
        : null;
      const opensOtherLocalities = interestRegionColumn.key === 'interestRegionOther';

      return (
        <td key={key} className="px-4 py-3 text-right font-mono">
          {value === null ? '—' : opensOtherLocalities ? (
            <button
              type="button"
              className="whitespace-nowrap underline decoration-dotted underline-offset-2 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              aria-label={`Ver outras localidades (${formatNumber(value)})`}
              onClick={() => void openScopedOtherLocalities(row, scope)}
            >
              {formatNumber(value)}
            </button>
          ) : formatNumber(value)}
        </td>
      );
    }

    const contacts = contactValue(row);
    const cpfReceived = numberValue(row.lead_com_cpf);
    const multiAttributedCpfContacts = usesLifetimeFirstTouch
      ? row.lead_com_cpf_multiatribuido
      : undefined;
    const cleanCpf = numberValue(row.lead_com_cpf_limpo);
    const sentFolders = sentFoldersValue(row);
    const approvedFolders = approvedFoldersValue(row);
    const appointments = numberValue(row.agendamentos_presenciais);
    const purchases = purchasesValue(row);

    if (key === 'spend') return <td key={key} className="px-4 py-3 text-right font-mono">{formatMoney(row.gasto, 'R$ 0,00')}</td>;
    if (key === 'impressions') return <td key={key} className="px-4 py-3 text-right font-mono">{formatNumber(row.impressions)}</td>;
    if (key === 'uniqueClicks') return <td key={key} className="px-4 py-3 text-right font-mono">{formatNumber(row.unique_clicks)}</td>;
    if (key === 'ctr') return <td key={key} className="px-4 py-3 text-right font-mono">{formatPercent(row.ctr)}</td>;
    if (key === 'messages') {
      return usesLifetimeFirstTouch
        ? <FirstTouchContactCell key={key} row={row} />
        : <td key={key} className="px-4 py-3 text-right font-mono">{formatNumber(contacts)}</td>;
    }
    if (key === 'messageCost') return <td key={key} className="px-4 py-3 text-right font-mono">{formatMoney(messageCost(row), '-')}</td>;
    if (key === 'cpfReceived') {
      return (
        <FunnelMetricCell
          key={key}
          funnelBase={contacts}
          multiAttributedCount={multiAttributedCpfContacts}
          quantity={cpfReceived}
        />
      );
    }
    if (key === 'cpfReceivedCost') return <td key={key} className="px-4 py-3 text-right font-mono">{formatMoney(cpfReceivedCost(row), '-')}</td>;
    if (key === 'cleanCpf') return <FunnelMetricCell key={key} funnelBase={contacts} previousBase={cpfReceived} quantity={cleanCpf} showPreviousConversion />;
    if (key === 'cleanCpfCost') return <td key={key} className="px-4 py-3 text-right font-mono">{formatMoney(cleanCpfCost(row), '-')}</td>;
    if (key === 'sentFolders') return <FunnelMetricCell key={key} funnelBase={contacts} previousBase={cleanCpf} quantity={sentFolders} showPreviousConversion />;
    if (key === 'sentCost') return <td key={key} className="px-4 py-3 text-right font-mono">{formatMoney(sentCost(row))}</td>;
    if (key === 'avgSentTime') return <TimeMetricCell key={key} row={row} stage="sent" valueKey="tempo_medio_pasta_enviada_dias" valueLabel="Tempo médio para pasta enviada" />;
    if (key === 'medianSentTime') return <TimeMetricCell key={key} row={row} stage="sent" valueKey="mediana_tempo_pasta_enviada_dias" valueLabel="Mediana de tempo para pasta enviada" />;
    if (key === 'approvedFolders') return <FunnelMetricCell key={key} funnelBase={contacts} previousBase={sentFolders} quantity={approvedFolders} showPreviousConversion />;
    if (key === 'approvedCost') return <td key={key} className="px-4 py-3 text-right font-mono font-semibold">{formatMoney(approvedCost(row))}</td>;
    if (key === 'avgApprovedTime') return <TimeMetricCell key={key} row={row} stage="approved" valueKey="tempo_medio_pasta_aprovada_dias" valueLabel="Tempo médio para pasta aprovada" />;
    if (key === 'medianApprovedTime') return <TimeMetricCell key={key} row={row} stage="approved" valueKey="mediana_tempo_pasta_aprovada_dias" valueLabel="Mediana de tempo para pasta aprovada" />;
    if (key === 'appointments') return <FunnelMetricCell key={key} funnelBase={contacts} previousBase={approvedFolders} quantity={appointments} showPreviousConversion />;
    if (key === 'purchases') return <FunnelMetricCell key={key} funnelBase={contacts} previousBase={appointments} quantity={purchases} showPreviousConversion />;
    if (key === 'purchaseCost') return <td key={key} className="px-4 py-3 text-right font-mono font-semibold">{formatMoney(purchaseCost(row))}</td>;
    if (key === 'recommendation') return <td key={key} className="px-4 py-3"><ActionBadge action={row.acao_recomendada} /></td>;
    return null;
  }

  function renderCampaignLine(group: CampaignGroup) {
    const opened = openCampaigns[group.id];

    return (
      <tr key={group.id} className="border-t border-border-subtle hover:bg-accent">
        {visibleCampaignColumns.map((column) => {
          if (column.key !== 'entity') return renderMetricCell(group.campaign, column.key, 'campaign');

          return (
            <td key={column.key} className={classNames('sticky left-0 z-20 bg-card px-4 py-3 font-medium text-textPrimary shadow-[1px_0_0_hsl(var(--border))]', campaignEntityColumnWidthClass)}>
              <div className="flex items-start gap-1.5">
                {group.adsets.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setOpenCampaigns((current) => ({ ...current, [group.id]: !current[group.id] }))}
                    className="rounded p-0.5 text-textSecondary hover:bg-muted hover:text-textPrimary"
                    aria-label={opened ? 'Recolher campanha' : 'Expandir campanha'}
                  >
                    <i className={opened ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} aria-hidden />
                  </button>
                )}
                <div className="min-w-0 space-y-1">
                  <span className="block whitespace-normal break-words leading-snug">{firstText(group.campaign, ['campaign_name', 'campaign_id'])}</span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {group.campaign.is_unattributed === true ? (
                      <span className="inline-flex items-center gap-1 rounded border border-amber-300/50 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-100">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
                        Sem vínculo de mídia
                      </span>
                    ) : (
                      <>
                        <MarketingEntityStatusBadge level="campaign" row={group.campaign} />
                        <span className="text-[11px] font-normal text-textSecondary">{group.adsets.length} adset(s)</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    );
  }

  function renderAdsetLine(adset: CampaignAdsetGroup) {
    const opened = openAdsets[adset.id];

    return (
      <tr key={adset.id} className="border-t border-border-subtle bg-muted/30 hover:bg-accent">
        {visibleCampaignColumns.map((column) => {
          if (column.key !== 'entity') return renderMetricCell(adset.row, column.key, 'adset');

          return (
            <td key={column.key} className={classNames('sticky left-0 z-20 bg-muted px-4 py-3 pl-10 text-textSecondary shadow-[1px_0_0_hsl(var(--border))]', campaignEntityColumnWidthClass)}>
              <div className="flex items-start gap-1.5">
                {adset.creatives.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setOpenAdsets((current) => ({ ...current, [adset.id]: !current[adset.id] }))}
                    className="rounded p-0.5 text-textSecondary hover:bg-muted hover:text-textPrimary"
                    aria-label={opened ? 'Recolher adset' : 'Expandir adset'}
                  >
                    <i className={opened ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} aria-hidden />
                  </button>
                )}
                <div className="min-w-0">
                  <span className="block whitespace-normal break-words font-medium leading-snug text-textPrimary">{firstText(adset.row, ['adset_name', 'adset_id'])}</span>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <MarketingEntityStatusBadge level="adset" row={adset.row} />
                    <CreativeStatusBreakdown rows={adset.creatives} />
                  </div>
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    );
  }

  function renderCreativeLine(row: MarketingRow, index: number, adset: CampaignAdsetGroup) {
    const previewUrl = creativePreviewUrl(row);

    return (
      <tr key={`${adset.id}-${safeRowKey(row, index)}`} className="border-t border-border-subtle bg-card hover:bg-accent">
        {visibleCampaignColumns.map((column) => {
          if (column.key !== 'entity') return renderMetricCell(row, column.key, 'creative');

          return (
            <td key={column.key} className={classNames('sticky left-0 z-20 bg-card px-4 py-3 pl-16 shadow-[1px_0_0_hsl(var(--border))]', campaignEntityColumnWidthClass)}>
              <div className="flex items-start gap-3">
                <CreativePreview row={row} className="h-10 w-10" />
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <CreativeStatusBadge row={row} />
                    <span className="min-w-0 whitespace-normal break-words font-medium leading-snug text-textPrimary">{creativeName(row)}</span>
                  </div>
                  <p className="whitespace-normal break-words text-xs text-textSecondary">
                    ID {firstText(row, ['creative_id', 'ad_id'])} · criado em {creativeCreatedAt(row)}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-textSecondary">
                    <span className="min-w-0 break-words">{firstText(row, ['ad_name'])}</span>
                    {previewUrl && <CreativePreviewLink row={row} />}
                  </div>
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    );
  }

  return (
    <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
      <div className="flex flex-col gap-3 p-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h3 className="font-fraunces text-base font-semibold text-textPrimary">Campanhas e Adsets</h3>
          <p className="font-instrument text-xs text-textSecondary">Expanda campanhas e adsets para ver os criativos com métricas de mídia.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2" aria-label="Resumo de status dos itens exibidos">
            <MarketingStatusCountsSummary counts={campaignStatusCounts} label="Campanhas" level="campaign" />
            <MarketingStatusCountsSummary counts={adsetStatusCounts} label="Adsets" level="adset" />
            {usesLifetimeFirstTouch ? (
              <span
                className="inline-flex items-center gap-1 rounded border border-sky-300/50 bg-sky-500/10 px-1.5 py-0.5 font-instrument text-[10px] font-medium text-sky-800 dark:text-sky-100"
                title="As conversões permanecem vinculadas à campanha, ao anúncio e ao criativo da primeira interação registrada na vida do contato."
              >
                <i className="ri-git-commit-line" aria-hidden />
                Atribuição: primeira interação
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1 font-instrument text-[11px] text-textSecondary">
              <i className="ri-sort-desc" aria-hidden />
              Ativos primeiro
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <div ref={columnMenuRef} className="relative">
            <HabitaButton
              aria-expanded={isColumnMenuOpen}
              aria-controls={isColumnMenuOpen ? 'campaign-columns-popover' : undefined}
              aria-haspopup="dialog"
              icon="ri-layout-column-line"
              size="sm"
              variant="secondary"
              onClick={() => setIsColumnMenuOpen((value) => !value)}
            >
              Colunas
            </HabitaButton>
            {isColumnMenuOpen && columnMenuPosition && typeof document !== 'undefined' && createPortal(
              <div
                ref={columnPopoverRef}
                className="fixed z-[120] flex overflow-hidden rounded-lg border border-border-subtle bg-card shadow-xl"
                style={columnMenuPosition}
                aria-label="Selecionar colunas visíveis"
                id="campaign-columns-popover"
                role="dialog"
              >
                <div className="flex min-h-0 w-full flex-col">
                  <div className="flex items-center justify-between gap-3 border-b border-border-subtle px-3 py-2">
                    <p className="font-instrument text-xs font-semibold text-textPrimary">Colunas visíveis</p>
                    <span className="font-instrument text-[11px] text-textSecondary">{visibleColumnKeys.length}/{campaignTableColumns.length}</span>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-2">
                    {columnOrderKeys.map((columnKey) => {
                      const column = campaignColumnByKey.get(columnKey);
                      if (!column) return null;

                      const checked = visibleColumnKeys.includes(column.key);
                      const disabled = checked && visibleColumnKeys.length === 1;
                      const inputId = `campaign-column-${column.key}`;
                      const isDragging = draggingColumnKey === column.key;
                      const showDropBefore = columnDropTarget?.key === column.key
                        && columnDropTarget.placement === 'before'
                        && draggingColumnKey !== column.key;
                      const showDropAfter = columnDropTarget?.key === column.key
                        && columnDropTarget.placement === 'after'
                        && draggingColumnKey !== column.key;

                      return (
                        <div
                          key={column.key}
                          draggable
                          onDragStart={(event) => startColumnDrag(event, column.key)}
                          onDragOver={(event) => updateColumnDropTarget(event, column.key)}
                          onDragLeave={() => setColumnDropTarget((current) => current?.key === column.key ? null : current)}
                          onDrop={(event) => dropColumn(event, column.key)}
                          onDragEnd={endColumnDrag}
                          className={classNames(
                            'relative flex cursor-grab items-center gap-2 rounded-md px-2 py-1.5 font-instrument text-xs text-textPrimary hover:bg-muted active:cursor-grabbing',
                            isDragging && 'opacity-50'
                          )}
                        >
                          {showDropBefore && <span className="absolute left-2 right-2 top-0 h-0.5 rounded-full bg-primary" />}
                          {showDropAfter && <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary" />}
                          <i className="ri-drag-move-2-line text-base text-textSecondary" aria-hidden />
                          <input
                            id={inputId}
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleColumnVisibility(column.key)}
                            className="h-4 w-4 rounded border-border-subtle accent-primary"
                          />
                          <label
                            htmlFor={inputId}
                            className={classNames(
                              'min-w-0 flex-1 cursor-pointer truncate',
                              disabled && 'cursor-not-allowed opacity-60'
                            )}
                          >
                            {column.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            , document.body)}
          </div>
          <HabitaButton
            icon={allExpanded ? 'ri-subtract-line' : 'ri-expand-up-down-line'}
            size="sm"
            onClick={toggleAllRows}
          >
            {allExpanded ? 'Recolher tudo' : 'Expandir tudo'}
          </HabitaButton>
        </div>
      </div>
      <div ref={tableScrollRef} className="marketing-campaign-table-scroll max-h-[70vh] overflow-x-auto overflow-y-auto" onScroll={() => syncTableScroll('table')}>
        <table className={classNames(visibleCampaignColumns.length > 8 ? 'min-w-[1900px]' : 'min-w-full', 'border-collapse font-instrument text-sm')}>
          <thead className={`border-b ${habitaControlBorderClass} bg-muted text-xs text-textSecondary`}>
            <tr>
              {visibleCampaignColumns.map((column) => (
                <th
                  key={column.key}
                  className={classNames(
                    'sticky top-0 whitespace-nowrap bg-muted px-4 py-2.5 font-medium',
                    column.key === 'entity'
                      ? classNames('left-0 z-40 !whitespace-normal text-left shadow-[1px_0_0_hsl(var(--border))]', campaignEntityColumnWidthClass)
                      : 'z-30 text-right'
                  )}
                >
                  {column.key === 'interestRegionOther' ? (
                    <button
                      type="button"
                      className="underline decoration-dotted underline-offset-2 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      onClick={openAllOtherLocalities}
                    >
                      {column.label}
                    </button>
                  ) : column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.id}>
                {renderCampaignLine(group)}
                {openCampaigns[group.id] && group.adsets.map((adset) => (
                  <Fragment key={adset.id}>
                    {renderAdsetLine(adset)}
                    {openAdsets[adset.id] && adset.creatives.map((row, index) => renderCreativeLine(row, index, adset))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {hasHorizontalOverflow && (
        <div className="sticky bottom-0 z-30 border-t border-border-subtle bg-card px-4 py-1">
          <div
            ref={floatingScrollRef}
            aria-hidden="true"
            className="h-3 overflow-x-auto overflow-y-hidden"
            onScroll={() => syncTableScroll('floating')}
          >
            <div style={{ height: 1, width: tableScrollWidth || '100%' }} />
          </div>
        </div>
      )}
      <OtherLocalitiesDialog
        contextLabel={otherLocalitiesDialog.contextLabel}
        error={otherLocalitiesDialog.error}
        isLoading={otherLocalitiesDialog.isLoading}
        isOpen={otherLocalitiesDialog.isOpen}
        items={otherLocalitiesDialog.items}
        onClose={closeOtherLocalitiesDialog}
      />
    </section>
  );
}

function CreativePerformanceBadge({ cost, max, min }: { cost: number | null; max: number; min: number }) {
  const label = cost === null ? 'Sem desempenho' : cost === min ? 'Alto desempenho' : cost === max ? 'Baixo desempenho' : 'Médio';
  const className = cost === min
    ? 'border-emerald-300/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
    : cost === max
      ? 'border-red-300/40 bg-red-500/10 text-red-700 dark:text-red-200'
      : 'border-border-subtle bg-muted text-textSecondary';

  return <span className={classNames('inline-flex rounded-full border px-2 py-1 font-instrument text-[11px] font-medium', className)}>{label}</span>;
}

function CompareRow({ label, strong, value }: { label: string; strong?: boolean; value: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] items-start gap-3">
      <dt className="min-w-0 text-textSecondary">{label}</dt>
      <dd className={classNames('min-w-0 break-words text-right font-mono text-textPrimary', strong && 'font-semibold')} title={value}>{value}</dd>
    </div>
  );
}

function CreativeMetricsRows({ row }: { row: MarketingRow }) {
  return (
    <>
      <CompareRow label="Campanha" value={firstText(row, ['campaign_name', 'campaign_id'])} />
      <CompareRow label="Conjunto" value={firstText(row, ['adset_name', 'adset_id'])} />
      <CompareRow label="Anúncio" value={firstText(row, ['ad_name', 'ad_id'])} />
      <CompareRow label="ID Criativo" value={firstText(row, ['creative_id', 'ad_id'])} />
      <CompareRow label="Criado em" value={creativeCreatedAt(row)} />
      <CompareRow label="Preview" value={creativePreviewUrl(row) ? 'Disponível' : 'Sem preview'} />
      <CompareRow label="Quality" value={creativeQuality(row)} />
      <CompareRow label="Conversation" value={creativeConversationRate(row)} />
    </>
  );
}

function CreativeResultRows({ row }: { row: MarketingRow }) {
  return (
    <>
      <CompareRow label="Impressões" value={formatNumber(row.impressions)} />
      <CompareRow label="Unique cliques" value={formatNumber(row.unique_clicks)} />
      <CompareRow label="CTR" value={formatPercent(row.ctr)} />
      <CompareRow label="Contatos" value={formatNumber(contactValue(row))} />
      <CompareRow label="Pastas Aprovadas" value={formatNumber(row.pastas_aprovadas ?? row.pastas_aprovadas_estimadas, optionalNumber(row.pastas_aprovadas) === null ? 2 : 0)} />
      <CompareRow label="Custo/Pasta Aprovada" value={rowApprovedCostLabel(row)} strong />
      <CompareRow label="Vendas" value={formatNumber(purchasesValue(row))} />
      <CompareRow label="Custo/Venda" value={formatMoney(purchaseCost(row))} strong />
      <CompareRow label="Gasto" value={formatMoney(row.gasto, 'R$ 0,00')} />
    </>
  );
}

function AdsCreativePrototypeTab({ rows }: { rows: MarketingRow[] }) {
  const costs = rows.map(approvedCost).filter((value): value is number => value !== null);
  const min = costs.length ? Math.min(...costs) : Number.POSITIVE_INFINITY;
  const max = costs.length ? Math.max(...costs) : Number.NEGATIVE_INFINITY;
  const ordered = [...rows].sort((left, right) => (approvedCost(left) ?? Number.POSITIVE_INFINITY) - (approvedCost(right) ?? Number.POSITIVE_INFINITY));
  const best = ordered[0];
  const worst = ordered[ordered.length - 1];
  const [selected, setSelected] = useState<string[]>([]);
  const [compare, setCompare] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
  const selectedRows = rows.filter((row, index) => selected.includes(safeRowKey(row, index)));

  if (rows.length === 0) {
    return <HabitaEmptyState icon="ri-image-line" title="Nenhum anúncio encontrado" description="Ajuste os filtros ou tente outro período." />;
  }

  function toggle(row: MarketingRow, index: number) {
    const key = safeRowKey(row, index);
    setSelected((current) => current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key]);
  }

  function toggleDescription(key: string) {
    setExpandedDescriptions((current) => ({ ...current, [key]: !current[key] }));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-fraunces text-base font-semibold text-textPrimary">Anúncios e Criativos</h3>
          <p className="font-instrument text-xs text-textSecondary">Selecione criativos para comparar lado a lado.</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && <span className="font-instrument text-xs text-textSecondary">{selected.length} selecionado(s)</span>}
          {selected.length > 0 && (
            <HabitaButton icon="ri-close-line" onClick={() => setSelected([])} size="sm" variant="secondary">
              Limpar
            </HabitaButton>
          )}
          <HabitaButton icon="ri-scales-3-line" disabled={selected.length < 2} onClick={() => setCompare((value) => !value)} size="sm" variant={compare ? 'primary' : 'secondary'}>
            {compare ? 'Fechar comparação' : 'Comparar'}
          </HabitaButton>
        </div>
      </div>

      {best && worst && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <MetricCard icon="ri-trophy-line" label="Melhor desempenho" value={creativeName(best)} description={rowApprovedCostLabel(best)} tone="success" />
          <MetricCard icon="ri-arrow-down-circle-line" label="Pior desempenho" value={creativeName(worst)} description={rowApprovedCostLabel(worst)} tone="warning" />
        </div>
      )}

      {compare && selectedRows.length >= 2 ? (
        <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <div className="grid auto-cols-[minmax(280px,320px)] grid-flow-col">
              {selectedRows.map((row, index) => (
                <div key={safeRowKey(row, index)} className="min-w-0 space-y-3 border-r border-border-subtle p-4 last:border-r-0">
                  <CreativePreview row={row} className="h-32 w-full" />
                  <div className="flex flex-wrap gap-1.5">
                    <CreativePerformanceBadge cost={approvedCost(row)} max={max} min={min} />
                    <CreativeStatusBadge row={row} />
                  </div>
                  <div>
                    <p className="break-words font-instrument text-sm font-semibold text-textPrimary">{creativeName(row)}</p>
                    <p className="line-clamp-3 break-words font-instrument text-xs text-textSecondary">{creativeBody(row)}</p>
                  </div>
                  <dl className="space-y-1 font-instrument text-xs">
                    <CreativeMetricsRows row={row} />
                    <CreativeResultRows row={row} />
                  </dl>
                  <div className="font-instrument text-xs">
                    <CreativePreviewLink row={row} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((row, index) => {
            const key = safeRowKey(row, index);
            const isSelected = selected.includes(key);
            const body = creativeBody(row);
            const canExpandDescription = body !== '-' && body.length > 80;
            const descriptionExpanded = expandedDescriptions[key] ?? false;

            return (
              <article key={key} className={classNames(habitaSurfaceClass, 'overflow-hidden p-4', isSelected && 'ring-2 ring-primary')}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    <CreativePerformanceBadge cost={approvedCost(row)} max={max} min={min} />
                    <CreativeStatusBadge row={row} />
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(row, index)}
                    className="h-4 w-4 rounded border-border-subtle accent-primary"
                    aria-label="Selecionar criativo"
                  />
                </div>
                <CreativePreview row={row} className="mt-3 h-36 w-full" />
                <div className="mt-3">
                  <p className="font-instrument text-xs text-textSecondary">{firstText(row, ['campaign_name', 'campaign_id'])}</p>
                  <p className="line-clamp-2 font-instrument text-sm font-semibold leading-tight text-textPrimary">{creativeName(row)}</p>
                  <p className={classNames('mt-1 break-words font-instrument text-xs text-textSecondary', !descriptionExpanded && 'line-clamp-2')}>{body}</p>
                  {canExpandDescription && (
                    <button
                      type="button"
                      className="mt-1 font-instrument text-xs font-medium text-primary hover:underline"
                      onClick={() => toggleDescription(key)}
                    >
                      {descriptionExpanded ? 'Recolher descrição' : 'Expandir descrição'}
                    </button>
                  )}
                </div>
                <dl className="mt-3 space-y-1.5 border-t border-border-subtle pt-2 font-instrument text-xs">
                  <CreativeMetricsRows row={row} />
                </dl>
                <div className="mt-3 space-y-2 border-t border-border-subtle pt-2 font-instrument text-xs">
                  <CreativeResultRows row={row} />
                  <div className="text-[11px]">
                    <CreativePreviewLink row={row} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HeatLegend() {
  return (
    <div className="mt-3 flex items-center gap-2 font-instrument text-[11px] text-textSecondary">
      <span>Menor custo</span>
      <div className="h-2 flex-1 rounded" style={{ background: 'linear-gradient(90deg, hsl(145 60% 55%), hsl(72 60% 55%), hsl(0 60% 55%))' }} />
      <span>Maior custo</span>
    </div>
  );
}

function audienceName(row: MarketingRow): string {
  const age = translateAgeLabel(rowText(row, 'age'));
  const gender = translateGenderLabel(rowText(row, 'gender'));
  if (age !== '-' && gender !== '-') return `${age} · ${gender}`;
  if (age !== '-') return age;
  if (gender !== '-') return gender;
  return 'Público sem identificação';
}

function audienceApprovedVolume(row: MarketingRow): string {
  const approved = row.pastas_aprovadas ?? row.pastas_aprovadas_estimadas;
  const isEstimated = optionalNumber(row.pastas_aprovadas) === null && optionalNumber(row.pastas_aprovadas_estimadas) !== null;
  return `${formatNumber(approved, isEstimated ? 2 : 0)} ${isEstimated ? 'aprovadas estimadas' : 'aprovadas'}`;
}

function buildAudienceTopRows(rows: MarketingRow[], limit = 8): AudienceBarPoint[] {
  const rankedRows = rows
    .map((row) => ({ cost: approvedCost(row), row }))
    .filter((item): item is { cost: number; row: MarketingRow } => item.cost !== null)
    .sort((left, right) => left.cost - right.cost)
    .slice(0, limit);
  const costs = rankedRows.map((item) => item.cost);
  const min = costs.length ? Math.min(...costs) : 0;
  const max = costs.length ? Math.max(...costs) : 0;

  return rankedRows.map(({ cost, row }) => ({
    approvedLabel: audienceApprovedVolume(row),
    cost,
    fill: heatColor(cost, min, max),
    gasto: row.gasto,
    name: audienceName(row),
    row,
  }));
}

function AudienceCostTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: AudienceBarTooltipPayload[];
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  return (
    <HabitaChartTooltipSurface>
      <p className="mb-1 font-semibold">{point.name}</p>
      <p>Custo/Pasta Aprovada: <span className="font-mono font-semibold">{formatMoney(point.cost)}</span></p>
      <p className="mt-1 text-textSecondary">{point.approvedLabel} · {formatMoney(point.gasto, 'R$ 0,00')}</p>
    </HabitaChartTooltipSurface>
  );
}

function AudienceTopPublicsBlock({ rows }: { rows: MarketingRow[] }) {
  const chartRows = buildAudienceTopRows(rows);

  return (
    <section className={classNames(habitaSurfaceClass, 'h-full')}>
      <HabitaSectionHeader title="Custo por pasta aprovada — top públicos" titleClassName="text-base" />
      <div className="p-5">
        {chartRows.length === 0 ? (
          <p className="p-4 font-instrument text-sm text-textSecondary">Nenhum público com pasta aprovada encontrado.</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartRows} layout="vertical" margin={{ bottom: 4, left: 28, right: 16, top: 4 }}>
              <CartesianGrid horizontal={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis
                axisLine={false}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                tickFormatter={axisMoney}
                tickLine={false}
                type="number"
              />
              <YAxis
                axisLine={false}
                dataKey="name"
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                tickLine={false}
                type="category"
                width={112}
              />
              <Tooltip content={<AudienceCostTooltip />} />
              <Bar dataKey="cost" name="Custo/Pasta Aprovada" radius={[0, 4, 4, 0]}>
                {chartRows.map((point, index) => <Cell key={`${point.name}-${index}`} fill={point.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

function AudienceRankingBlock({ rows }: { rows: MarketingRow[] }) {
  const topRows = buildAudienceTopRows(rows, 9);

  if (topRows.length === 0) return null;

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader title="Ranking de públicos" titleClassName="text-base" />
      <div className="grid grid-cols-1 gap-2 p-5 sm:grid-cols-2 lg:grid-cols-3">
        {topRows.map((point, index) => (
          <article key={`${point.name}-${index}`} className="flex items-center justify-between gap-3 rounded-md border border-border-subtle p-2.5">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted font-instrument text-xs font-semibold text-textPrimary">
                {index + 1}
              </span>
              <div className="min-w-0">
                <p className="truncate font-instrument text-sm font-medium text-textPrimary">{point.name}</p>
                <p className="truncate font-instrument text-xs text-textSecondary">{point.approvedLabel} · {formatMoney(point.gasto, 'R$ 0,00')}</p>
              </div>
            </div>
            <span className="shrink-0 font-mono text-sm font-semibold text-textPrimary">{formatMoney(point.cost)}</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function AudiencePrototypeTab({ ageGender }: { ageGender: MarketingRowsResponse }) {
  const rows = ageGender.rows;
  const ages = Array.from(new Set(rows.map((row) => rowText(row, 'age')).filter((value) => value !== '-'))).sort();
  const genders = Array.from(new Set(rows.map((row) => rowText(row, 'gender')).filter((value) => value !== '-'))).sort();
  const costs = rows.map(approvedCost).filter((value): value is number => value !== null);
  const min = costs.length ? Math.min(...costs) : 0;
  const max = costs.length ? Math.max(...costs) : 0;

  if (rows.length === 0) {
    return <HabitaEmptyState icon="ri-group-line" title="Nenhum público encontrado" description="Ajuste os filtros ou tente outro período." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-fraunces text-base font-semibold text-textPrimary">Público</h3>
          <p className="font-instrument text-xs text-textSecondary">Custo por pasta aprovada por idade e gênero.</p>
        </div>
        <EstimatedNotice notice={ageGender.notice ?? 'Dados estimados'} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className={classNames(habitaSurfaceClass, 'p-5')}>
          <h4 className="font-fraunces text-base font-semibold text-textPrimary">Heatmap - Idade x Gênero</h4>
          <div className="mt-4 grid" style={{ gridTemplateColumns: `80px repeat(${Math.max(genders.length, 1)}, 1fr)` }}>
            <div />
            {genders.map((gender) => <div key={gender} className="px-2 pb-2 text-center font-instrument text-xs font-medium text-textPrimary">{translateGenderLabel(gender)}</div>)}
            {ages.map((age) => (
              <div key={age} className="contents">
                <div className="flex items-center pr-2 font-instrument text-xs font-medium text-textSecondary">{translateAgeLabel(age)}</div>
                {genders.map((gender) => {
                  const row = rows.find((item) => rowText(item, 'age') === age && rowText(item, 'gender') === gender);
                  const cost = row ? approvedCost(row) : null;
                  return (
                    <div key={gender} className="m-0.5 flex h-12 items-center justify-center rounded text-center font-mono text-xs font-semibold text-white" style={{ backgroundColor: heatColor(cost, min, max) }}>
                      {formatMoney(cost)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <HeatLegend />
        </section>

        <AudienceTopPublicsBlock rows={rows} />
      </div>

      <AudienceRankingBlock rows={rows} />
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div className="h-2 overflow-hidden rounded-full bg-muted">
      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
    </div>
  );
}

function PlacementPrototypeTab({ placement }: { placement: MarketingRowsResponse }) {
  const rows = [...placement.rows].sort((left, right) => (approvedCost(left) ?? Number.POSITIVE_INFINITY) - (approvedCost(right) ?? Number.POSITIVE_INFINITY));
  const bestCost = approvedCost(rows[0] ?? {}) ?? 0;

  if (rows.length === 0) {
    return <HabitaEmptyState icon="ri-layout-masonry-line" title="Nenhum posicionamento encontrado" description="Ajuste os filtros ou tente outro período." />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-fraunces text-base font-semibold text-textPrimary">Posicionamentos por pasta aprovada</h3>
        <p className="font-instrument text-xs text-textSecondary">Comparação de custo por pasta aprovada e desempenho por posicionamento.</p>
      </div>
      <EstimatedNotice notice={placement.notice} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className={classNames(habitaSurfaceClass, 'p-5')}>
          <h4 className="font-fraunces text-base font-semibold text-textPrimary">Desempenho por posicionamento por pasta aprovada</h4>
          <div className="mt-4 space-y-4">
            {rows.map((row, index) => {
              const cost = approvedCost(row);
              const efficiency = cost && bestCost ? Math.round((bestCost / cost) * 100) : 0;
              const label = [rowText(row, 'publisher_platform'), rowText(row, 'platform_position'), rowText(row, 'device_platform')]
                .filter((value) => value !== '-')
                .join(' · ');

              return (
                <div key={safeRowKey(row, index)}>
                  <div className="mb-1 flex items-center justify-between gap-3 font-instrument text-sm">
                    <span className="font-medium text-textPrimary">{label || firstText(row, ['placement', 'platform_position'])}</span>
                    <span className="font-mono text-textSecondary">{formatMoney(cost)}</span>
                  </div>
                  <ProgressBar value={efficiency} />
                </div>
              );
            })}
          </div>
        </section>

        <MarketingTable
          columns={[
            { label: 'Posicionamento', render: (row) => <span className="font-medium">{[rowText(row, 'publisher_platform'), rowText(row, 'platform_position'), rowText(row, 'device_platform')].filter((value) => value !== '-').join(' · ')}</span> },
            { label: 'Gasto', align: 'right', render: (row) => <span className="font-mono">{formatMoney(row.gasto, 'R$ 0,00')}</span> },
            { label: 'Pastas Aprovadas', align: 'right', render: (row) => <span className="font-mono">{formatNumber(row.pastas_aprovadas_estimadas, 2)}</span> },
            { label: 'Custo/Pasta Aprovada', align: 'right', render: (row) => <span className="font-mono font-semibold">{formatMoney(approvedCost(row))}</span> },
            { label: 'Cliques', align: 'right', render: (row) => <span className="font-mono">{formatNumber(row.inline_link_clicks)}</span> },
          ]}
          emptyDescription="Ajuste os filtros ou tente outro período."
          emptyTitle="Nenhum posicionamento encontrado"
          rows={rows}
        />
      </div>
    </div>
  );
}

const regionPositionClass: Record<string, string> = {
  Norte: 'col-start-1 col-end-3 row-start-1 row-end-3',
  Nordeste: 'col-start-3 col-end-5 row-start-1 row-end-3',
  'Centro-Oeste': 'col-start-2 col-end-3 row-start-3 row-end-4',
  Sudeste: 'col-start-3 col-end-4 row-start-3 row-end-4',
  Sul: 'col-start-3 col-end-4 row-start-4 row-end-5',
};

function RegionsPrototypeTab({ regions }: { regions: MarketingRowsResponse }) {
  const ordered = [...regions.rows].sort((left, right) => (approvedCost(left) ?? Number.POSITIVE_INFINITY) - (approvedCost(right) ?? Number.POSITIVE_INFINITY));
  const best = ordered[0];
  const worst = ordered[ordered.length - 1];
  const costs = ordered.map(approvedCost).filter((value): value is number => value !== null);
  const min = costs.length ? Math.min(...costs) : 0;
  const max = costs.length ? Math.max(...costs) : 0;

  if (ordered.length === 0) {
    return <HabitaEmptyState icon="ri-map-pin-line" title="Nenhuma região encontrada" description="A região aparecerá quando houver breakdown da Meta no período." />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-fraunces text-base font-semibold text-textPrimary">Regiões</h3>
        <p className="font-instrument text-xs text-textSecondary">Performance por região do Brasil - cor por custo por pasta aprovada.</p>
      </div>
      <EstimatedNotice notice={regions.notice} />
      {best && worst && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <MetricCard icon="ri-trophy-line" label="Melhor região" value={rowText(best, 'region')} description={formatMoney(approvedCost(best))} tone="success" />
          <MetricCard icon="ri-arrow-down-circle-line" label="Pior região" value={rowText(worst, 'region')} description={formatMoney(approvedCost(worst))} tone="warning" />
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className={classNames(habitaSurfaceClass, 'p-5')}>
          <h4 className="font-fraunces text-base font-semibold text-textPrimary">Mapa do Brasil</h4>
          <div className="mx-auto mt-4 grid aspect-square w-full max-w-sm gap-1.5" style={{ gridTemplateColumns: 'repeat(4,1fr)', gridTemplateRows: 'repeat(4,1fr)' }}>
            {ordered.map((row, index) => {
              const region = rowText(row, 'region');
              const cost = approvedCost(row);
              return (
                <div
                  key={`${region}-${index}`}
                  className={classNames(regionPositionClass[region], 'flex flex-col items-center justify-center rounded-lg p-2 text-center text-white')}
                  style={{ backgroundColor: heatColor(cost, min, max) }}
                >
                  <span className="font-instrument text-xs font-semibold leading-tight">{region}</span>
                  <span className="font-mono text-[11px]">{formatMoney(cost)}</span>
                </div>
              );
            })}
          </div>
          <HeatLegend />
        </section>

        <RankingList
          costKey="custo_estimado_por_pasta_aprovada"
          emptyLabel="Nenhuma região encontrada."
          labelKey="region"
          rows={ordered}
          subtitle={(row) => `${formatNumber(row.pastas_aprovadas_estimadas, 2)} aprovadas estimadas · ${formatMoney(row.gasto, 'R$ 0,00')}`}
          title="Ranking de regiões"
        />
      </div>
    </div>
  );
}

function CreativeRegionsPrototypeTab({ creativeRegions }: { creativeRegions: MarketingRowsResponse }) {
  const rows = creativeRegions.rows;
  const creativeKey = (row: MarketingRow) => firstText(row, ['creative_id', 'ad_id', 'creative_title', 'ad_name']);
  const creativeLabel = (row: MarketingRow) => firstText(row, ['creative_title', 'creative_name', 'ad_name', 'creative_id']);
  const creatives = Array.from(new Set(rows.map(creativeKey).filter((value) => value !== '-')));
  const regions = Array.from(new Set(rows.map((row) => rowText(row, 'region')).filter((value) => value !== '-')));
  const costs = rows.map(approvedCost).filter((value): value is number => value !== null);
  const min = costs.length ? Math.min(...costs) : 0;
  const max = costs.length ? Math.max(...costs) : 0;

  if (rows.length === 0) {
    return <HabitaEmptyState icon="ri-image-edit-line" title="Nenhum criativo por região encontrado" description="A combinação de criativo e região aparecerá quando houver spend suficiente." />;
  }

  return (
    <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
      <HabitaSectionHeader title="Criativos por Região" titleClassName="text-base">
        <span className="font-instrument text-xs text-textSecondary">Verde = melhor desempenho</span>
      </HabitaSectionHeader>
      <div className="space-y-4 overflow-x-auto p-5">
        <EstimatedNotice notice={creativeRegions.notice} />
        <div className="min-w-max">
          <div className="grid items-center gap-1.5" style={{ gridTemplateColumns: `180px repeat(${Math.max(regions.length, 1)}, 1fr)` }}>
            <div />
            {regions.map((region) => <div key={region} className="px-2 pb-1 text-center font-instrument text-xs font-semibold text-textPrimary">{region}</div>)}
            {creatives.map((creative) => {
              const firstRow = rows.find((row) => creativeKey(row) === creative) ?? {};
              return (
                <div key={creative} className="contents">
                  <div className="flex items-center gap-2 pr-2">
                    <CreativePreview row={firstRow} className="h-10 w-10" />
                    <span className="line-clamp-2 font-instrument text-xs font-medium text-textPrimary">{creativeLabel(firstRow)}</span>
                  </div>
                  {regions.map((region) => {
                    const row = rows.find((item) => creativeKey(item) === creative && rowText(item, 'region') === region);
                    const cost = row ? approvedCost(row) : null;
                    return (
                      <div key={region} className="m-0.5 flex h-12 min-w-[88px] items-center justify-center rounded text-center font-mono text-xs font-semibold text-white" style={{ backgroundColor: heatColor(cost, min, max) }}>
                        {formatMoney(cost)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <HeatLegend />
      </div>
    </section>
  );
}

const decisionGroups = [
  { group: 'Escalar', icon: 'ri-arrow-up-circle-line', tone: 'text-emerald-700 dark:text-emerald-300', border: 'border-l-emerald-500' },
  { group: 'Escalar com cautela', icon: 'ri-shield-star-line', tone: 'text-sky-700 dark:text-sky-300', border: 'border-l-sky-500' },
  { group: 'Revisar', icon: 'ri-search-line', tone: 'text-textSecondary', border: 'border-l-muted-foreground' },
  { group: 'Reduzir', icon: 'ri-arrow-down-circle-line', tone: 'text-amber-700 dark:text-amber-300', border: 'border-l-amber-500' },
  { group: 'Pausar', icon: 'ri-pause-circle-line', tone: 'text-red-700 dark:text-red-300', border: 'border-l-red-500' },
] as const;

function DecisionPrototypeTab({ matrix }: { matrix: MarketingDecisionMatrixResponse }) {
  const rows = summarizeDecisionRows(matrix);

  if (rows.length === 0) {
    return <HabitaEmptyState icon="ri-compass-3-line" title="Nenhuma recomendação no período" description="A matriz aparece quando há dados suficientes para priorização." />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-fraunces text-base font-semibold text-textPrimary">Matriz de Decisão</h3>
        <p className="font-instrument text-xs text-textSecondary">Recomendações priorizadas com motivo, métricas e ação sugerida.</p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {decisionGroups.map((item) => {
          const count = rows.filter((row) => recommendationGroup(row) === item.group).length;
          return (
            <section key={item.group} className={classNames(habitaSurfaceClass, 'border-l-4 p-3', item.border)}>
              <div className="flex items-center gap-2">
                <i className={`${item.icon} text-xl ${item.tone}`} aria-hidden />
                <div>
                  <p className="font-instrument text-lg font-semibold leading-none text-textPrimary">{count}</p>
                  <p className="font-instrument text-[11px] text-textSecondary">{item.group}</p>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <div className="space-y-4">
        {decisionGroups.map((item) => {
          const groupRows = rows.filter((row) => recommendationGroup(row) === item.group);
          if (groupRows.length === 0) return null;

          return (
            <section key={item.group} className="space-y-2">
              <div className="flex items-center gap-2">
                <i className={`${item.icon} ${item.tone}`} aria-hidden />
                <h4 className="font-instrument text-sm font-semibold text-textPrimary">{item.group}</h4>
                <span className="rounded-full border border-border-subtle px-2 py-0.5 font-instrument text-xs text-textSecondary">{groupRows.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {groupRows.slice(0, 8).map((row, index) => (
                  <article key={safeRowKey(row, index)} className={classNames(habitaSurfaceClass, 'space-y-2 border-l-4 p-4', item.border)}>
                    <p className="font-instrument text-sm font-semibold text-textPrimary">{firstText(row, ['ad_name', 'adset_name', 'campaign_name', 'region', 'age'])}</p>
                    <p className="font-instrument text-xs text-textSecondary">
                      {formatMoney(row.gasto, 'R$ 0,00')} · {formatNumber(row.pastas_aprovadas ?? row.pastas_aprovadas_estimadas, 2)} aprovadas · {rowApprovedCostLabel(row)}
                    </p>
                    <div className="rounded-md bg-muted/50 px-2.5 py-1.5 font-mono text-xs text-textPrimary">
                      {rowText(row, 'motivo') !== '-' ? rowText(row, 'motivo') : rowText(row, 'acao_recomendada')}
                    </div>
                    <div className="flex items-start gap-1.5 font-instrument text-xs">
                      <span className="shrink-0 font-medium text-textPrimary">Ação:</span>
                      <span className="text-textSecondary">{rowText(row, 'acao_recomendada')}</span>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function AuditTab({ audit }: { audit: MarketingAuditResponse }) {
  const referralHealth = audit.first_message_referral_health ?? {};
  const qualifiedHealth = audit.qualified_without_first_message ?? {};
  const duplicates = audit.duplicate_events ?? {};
  const regionLeakage = audit.region_leakage ?? {};
  const availableObjects = audit.objects.filter((item) => item.exists).length;
  const contactHealthTotal = referralHealth.contatos_total ?? referralHealth.first_messages_total;
  const coverage = numberValue(contactHealthTotal) > 0
    ? (numberValue(referralHealth.com_meta_ad) / numberValue(contactHealthTotal)) * 100
    : null;
  const latestUpdate = audit.freshness
    .map((row) => rowText(row, 'updated_at'))
    .filter((value) => value !== '-')
    .sort()
    .at(-1);
  const healthItems = [
    {
      area: 'Ingestão de gastos (Ads)',
      detail: `${availableObjects}/${audit.objects.length} objetos disponíveis`,
      status: availableObjects === audit.objects.length ? 'saudavel' : 'atencao',
    },
    {
      area: 'Conversões de pasta enviada',
      detail: `${formatNumber(qualifiedHealth.total_qualified_leads ?? qualifiedHealth.qualified_total)} leads qualificados`,
      status: numberValue(qualifiedHealth.qualified_sem_first_message) === 0 ? 'saudavel' : 'atencao',
    },
    {
      area: 'Atribuição por região',
      detail: `${formatNumber(regionLeakage.linhas_fora_praca_prioritaria)} linhas fora da praça`,
      status: numberValue(regionLeakage.linhas_fora_praca_prioritaria) === 0 ? 'saudavel' : 'atencao',
    },
    {
      area: 'Deduplicação de eventos',
      detail: `${formatNumber(duplicates.duplicate_event_groups)} grupos duplicados`,
      status: numberValue(duplicates.duplicate_event_groups) === 0 ? 'saudavel' : 'critico',
    },
  ] as const;
  const statusClass = {
    atencao: 'text-amber-700 dark:text-amber-300',
    critico: 'text-red-700 dark:text-red-300',
    saudavel: 'text-emerald-700 dark:text-emerald-300',
  };
  const statusIcon = {
    atencao: 'ri-alert-line',
    critico: 'ri-error-warning-line',
    saudavel: 'ri-checkbox-circle-line',
  };
  const statusLabel = {
    atencao: 'Atenção',
    critico: 'Crítico',
    saudavel: 'Saudável',
  };
  const inconsistencies = [
    {
      icon: numberValue(qualifiedHealth.qualified_sem_first_message) > 0 ? 'ri-alert-line' : 'ri-checkbox-circle-line',
      text: `${formatNumber(qualifiedHealth.qualified_sem_first_message)} eventos de pasta aprovada sem contato atribuído.`,
      tone: numberValue(qualifiedHealth.qualified_sem_first_message) > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300',
    },
    {
      icon: numberValue(duplicates.duplicate_event_groups) > 0 ? 'ri-error-warning-line' : 'ri-checkbox-circle-line',
      text: `${formatNumber(duplicates.duplicate_event_groups)} grupos de eventos duplicados.`,
      tone: numberValue(duplicates.duplicate_event_groups) > 0 ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300',
    },
    {
      icon: numberValue(regionLeakage.linhas_fora_praca_prioritaria) > 0 ? 'ri-alert-line' : 'ri-checkbox-circle-line',
      text: `${formatNumber(regionLeakage.linhas_fora_praca_prioritaria)} linhas fora da praça prioritária.`,
      tone: numberValue(regionLeakage.linhas_fora_praca_prioritaria) > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-fraunces text-base font-semibold text-textPrimary">Auditoria de Dados</h3>
          <p className="font-instrument text-xs text-textSecondary">Monitoramento da saúde, integrações e consistência dos dados.</p>
        </div>
        {latestUpdate && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle px-3 py-1.5 font-instrument text-xs text-textSecondary">
            <i className="ri-refresh-line" aria-hidden />
            Última atualização: {latestUpdate}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard icon="ri-checkbox-circle-line" label="Saúde geral" value={`${healthItems.filter((item) => item.status === 'saudavel').length}/${healthItems.length}`} description="áreas saudáveis" tone="success" />
        <MetricCard icon="ri-plug-line" label="Integrações" value={formatNumber(audit.freshness.length)} description="fontes sincronizadas" tone="info" />
        <MetricCard icon="ri-radar-line" label="Cobertura" value={coverage === null ? '-' : formatPercent(coverage)} description="eventos rastreados" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className={classNames(habitaSurfaceClass, 'p-5')}>
          <h4 className="flex items-center gap-2 font-fraunces text-base font-semibold text-textPrimary">
            <i className="ri-database-2-line text-textSecondary" aria-hidden />
            Saúde dos dados
          </h4>
          <div className="mt-4 space-y-2">
            {healthItems.map((item) => (
              <div key={item.area} className="flex items-center justify-between gap-3 rounded-md border border-border-subtle p-2.5">
                <div className="flex items-center gap-2.5">
                  <i className={`${statusIcon[item.status]} ${statusClass[item.status]}`} aria-hidden />
                  <div>
                    <p className="font-instrument text-sm font-medium text-textPrimary">{item.area}</p>
                    <p className="font-instrument text-xs text-textSecondary">{item.detail}</p>
                  </div>
                </div>
                <span className={`font-instrument text-xs font-medium ${statusClass[item.status]}`}>{statusLabel[item.status]}</span>
              </div>
            ))}
          </div>
        </section>

        <section className={classNames(habitaSurfaceClass, 'p-5')}>
          <h4 className="flex items-center gap-2 font-fraunces text-base font-semibold text-textPrimary">
            <i className="ri-plug-line text-textSecondary" aria-hidden />
            Status das integrações
          </h4>
          <div className="mt-4 space-y-2">
            {audit.freshness.length === 0 ? (
              <p className="font-instrument text-sm text-textSecondary">Sem atualização reportada.</p>
            ) : audit.freshness.map((row, index) => (
              <div key={`${rowText(row, 'table_name')}-${index}`} className="flex items-center justify-between gap-3 rounded-md border border-border-subtle p-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <div>
                    <p className="font-instrument text-sm font-medium text-textPrimary">{rowText(row, 'table_name')}</p>
                    <p className="font-instrument text-xs text-textSecondary">Sincronizado: {rowText(row, 'updated_at')}</p>
                  </div>
                </div>
                <span className="font-instrument text-xs font-medium text-emerald-700 dark:text-emerald-300">Saudável</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className={classNames(habitaSurfaceClass, 'p-5')}>
        <h4 className="font-fraunces text-base font-semibold text-textPrimary">Possíveis inconsistências</h4>
        <div className="mt-4 space-y-2">
          {inconsistencies.map((item, index) => (
            <div key={index} className="flex items-start gap-2.5 rounded-md bg-muted/30 p-2.5">
              <i className={`${item.icon} mt-0.5 shrink-0 ${item.tone}`} aria-hidden />
              <span className="font-instrument text-sm text-textPrimary">{item.text}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MarketingLoadingState() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-32 animate-pulse rounded-lg border border-border-subtle bg-card p-5">
            <div className="h-3 w-24 rounded bg-muted" />
            <div className="mt-5 h-8 w-20 rounded bg-muted" />
            <div className="mt-4 h-3 w-full rounded bg-muted/70" />
          </div>
        ))}
      </div>
      <div className="h-80 animate-pulse rounded-lg border border-border-subtle bg-card p-5">
        <div className="h-full rounded bg-muted/70" />
      </div>
    </div>
  );
}

function FilterPopoverField({
  label,
  onChange,
  onClear,
  options,
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  options: SelectOption[];
  value: string;
}) {
  return (
    <div className="space-y-1.5">
      <span className="font-instrument text-[11px] font-semibold uppercase tracking-wide text-textSecondary">{label}</span>
      <LabeledSelect
        label={label}
        value={value}
        options={options}
        onChange={onChange}
        onClear={onClear}
        className="w-full"
      />
    </div>
  );
}

function MarketingFiltersPopover({
  filters,
  hasActiveDimensions,
  onChange,
  onClearDimensions,
  onClearOne,
  open,
  options,
}: {
  filters: MarketingFilterState;
  hasActiveDimensions: boolean;
  onChange: <Key extends keyof MarketingFilterState>(key: Key, value: MarketingFilterState[Key]) => void;
  onClearDimensions: () => void;
  onClearOne: (key: keyof MarketingFilterState) => void;
  open: boolean;
  options: MarketingFilterOptionGroups;
}) {
  if (!open) return null;

  return (
    <div
      className="absolute right-0 top-full z-50 mt-2 w-[min(520px,calc(100vw-2rem))] rounded-lg border border-border-subtle bg-card p-4 text-textPrimary shadow-xl shadow-slate-900/10"
      role="dialog"
      aria-label="Filtros de marketing"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="font-instrument text-xs font-medium uppercase tracking-wide text-textSecondary">Estrutura</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <FilterPopoverField label="Conta" value={filters.accountId} options={options.accountId} onChange={(value) => onChange('accountId', value)} />
            <FilterPopoverField label="Campanha" value={filters.campaignId} options={options.campaignId} onChange={(value) => onChange('campaignId', value)} />
            <FilterPopoverField label="Adset" value={filters.adsetId} options={options.adsetId} onChange={(value) => onChange('adsetId', value)} />
            <FilterPopoverField label="Anúncio" value={filters.adId} options={options.adId} onChange={(value) => onChange('adId', value)} onClear={() => onClearOne('adId')} />
            <FilterPopoverField label="Criativo" value={filters.creativeId} options={options.creativeId} onChange={(value) => onChange('creativeId', value)} onClear={() => onClearOne('creativeId')} />
            <FilterPopoverField label="Status criativo" value={filters.creativeStatus} options={creativeStatusOptions} onChange={(value) => onChange('creativeStatus', value)} />
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-instrument text-xs font-medium uppercase tracking-wide text-textSecondary">Fonte</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <FilterPopoverField label="Fonte" value={filters.sourceChannel} options={options.sourceChannel} onChange={(value) => onChange('sourceChannel', value)} />
            <FilterPopoverField label="Formulário" value={filters.formId} options={options.formId} onChange={(value) => onChange('formId', value)} />
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-instrument text-xs font-medium uppercase tracking-wide text-textSecondary">Segmentação</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <FilterPopoverField label="Região" value={filters.region} options={options.region} onChange={(value) => onChange('region', value)} />
            <FilterPopoverField label="Plataforma" value={filters.publisherPlatform} options={options.publisherPlatform} onChange={(value) => onChange('publisherPlatform', value)} />
            <FilterPopoverField label="Posicionamento" value={filters.platformPosition} options={options.platformPosition} onChange={(value) => onChange('platformPosition', value)} />
            <FilterPopoverField label="Idade" value={filters.age} options={options.age} onChange={(value) => onChange('age', value)} />
            <FilterPopoverField label="Gênero" value={filters.gender} options={options.gender} onChange={(value) => onChange('gender', value)} />
          </div>
        </div>

        <div className="flex justify-end border-t border-border-subtle pt-3">
          <HabitaButton
            icon="ri-refresh-line"
            onClick={onClearDimensions}
            size="sm"
            variant="ghost"
            disabled={!hasActiveDimensions}
            className="text-textSecondary"
          >
            Limpar
          </HabitaButton>
        </div>
      </div>
    </div>
  );
}

function useCloseOnOutsideInteraction(
  isOpen: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onClose: () => void
) {
  useEffect(() => {
    if (!isOpen) return undefined;

    function closeOnOutsideInteraction(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (containerRef.current?.contains(target)) return;
      onClose();
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('mousedown', closeOnOutsideInteraction);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideInteraction);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [containerRef, isOpen, onClose]);
}

function clearDimensionFilters(filters: MarketingFilterState): MarketingFilterState {
  return {
    ...filters,
    accountId: '',
    adId: '',
    adsetId: '',
    age: '',
    campaignId: '',
    creativeId: '',
    creativeStatus: '',
    devicePlatform: '',
    formId: '',
    gender: '',
    platformPosition: '',
    publisherPlatform: '',
    region: '',
    sourceChannel: '',
  };
}

function getMarketingDashboardCacheKey(params: MarketingFetchParams): string {
  const stableParams = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([left], [right]) => left.localeCompare(right));

  return `${MARKETING_DASHBOARD_CACHE_PREFIX}${JSON.stringify(stableParams)}`;
}

function inferLoadedTabsFromDashboard(dashboard: MarketingDashboardData): MarketingTabLoadState {
  return {
    overview: true,
    ...(dashboard.campaigns?.rows?.length > 0 ? { campaigns: true } : {}),
    ...(dashboard.ads?.rows?.length > 0 ? { ads: true } : {}),
    ...(dashboard.ageGender?.rows?.length > 0 ? { audience: true } : {}),
    ...(dashboard.placement?.rows?.length > 0 ? { placement: true } : {}),
    ...(dashboard.interestRegions?.rows?.length > 0 ? { interestRegions: true } : {}),
    ...(dashboard.regions?.rows?.length > 0 ? { regions: true } : {}),
    ...(dashboard.creativeRegions?.rows?.length > 0 ? { creativeRegions: true } : {}),
    ...((dashboard.decisionMatrix?.campaigns?.length ?? 0) > 0
      || (dashboard.decisionMatrix?.adsets?.length ?? 0) > 0
      || (dashboard.decisionMatrix?.ads?.length ?? 0) > 0
      || (dashboard.decisionMatrix?.regions?.length ?? 0) > 0
      || (dashboard.decisionMatrix?.age_gender?.length ?? 0) > 0
      || (dashboard.decisionMatrix?.placement?.length ?? 0) > 0 ? { decision: true } : {}),
    ...((dashboard.audit?.freshness?.length ?? 0) > 0 || (dashboard.audit?.objects?.length ?? 0) > 0 ? { audit: true } : {}),
  };
}

function readMarketingDashboardCache(params: MarketingFetchParams): MarketingDashboardCacheSnapshot | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(getMarketingDashboardCacheKey(params));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MarketingDashboardCacheSnapshot | MarketingDashboardData;
    if (!parsed || typeof parsed !== 'object') return null;

    const hasCacheEnvelope = 'dashboard' in parsed;
    const dashboard = hasCacheEnvelope ? parsed.dashboard : parsed;
    if (!dashboard?.overview) return null;

    return {
      dashboard,
      loadedTabs: hasCacheEnvelope && parsed.loadedTabs ? parsed.loadedTabs : inferLoadedTabsFromDashboard(dashboard),
    };
  } catch {
    return null;
  }
}

function writeMarketingDashboardCache(
  params: MarketingFetchParams,
  dashboard: MarketingDashboardData,
  loadedTabs: MarketingTabLoadState = { overview: true }
): void {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;

  try {
    window.sessionStorage.setItem(getMarketingDashboardCacheKey(params), JSON.stringify({ dashboard, loadedTabs }));
  } catch {
    // Cache é apenas otimização de UX; falhas de quota/storage não devem afetar o painel.
  }
}

function keepLoadedTabData(
  base: MarketingDashboardData,
  current: MarketingDashboardData | null,
  loadedTabs: MarketingTabLoadState
): MarketingDashboardData {
  if (!current) return base;

  return {
    ...base,
    ...(loadedTabs.audit ? { audit: current.audit } : {}),
    ...(loadedTabs.audience ? { ageGender: current.ageGender } : {}),
    ...(loadedTabs.campaigns ? { campaigns: current.campaigns } : {}),
    ...(loadedTabs.creativeRegions ? { creativeRegions: current.creativeRegions } : {}),
    ...(loadedTabs.decision ? { decisionMatrix: current.decisionMatrix } : {}),
    ...(loadedTabs.interestRegions ? { interestRegions: current.interestRegions } : {}),
    ...(loadedTabs.placement ? { placement: current.placement } : {}),
    ...(loadedTabs.regions ? { regions: current.regions } : {}),
    ...(loadedTabs.ads ? { ads: current.ads } : {}),
  };
}

export default function Marketing() {
  const marketingRepository = useMemo(() => new MarketingRepository(), []);
  const [activeTab, setActiveTab] = useState<MarketingTab>('overview');
  const [filters, setFilters] = useState<MarketingFilterState>(initialMarketingFilters);
  const [dateBasis, setDateBasis] = useState<HabitaDateBasis>(savedMarketingDateBasis);
  const [data, setData] = useState<MarketingDashboardData | null>(null);
  const [filterOptionsData, setFilterOptionsData] = useState<MarketingFilterOptionsData | null>(null);
  const [filterOptionsError, setFilterOptionsError] = useState(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [filterOptionsRequest, setFilterOptionsRequest] = useState<MarketingFilterOptionsRequest | null>(null);
  const [filtersPopoverOpen, setFiltersPopoverOpen] = useState(false);
  const filtersPopoverRef = useRef<HTMLDivElement>(null);
  const [rankingLoadingLevel, setRankingLoadingLevel] = useState<PerformanceRankingLevel | null>(null);
  const rankingLoadingRef = useRef<PerformanceRankingLevel | null>(null);
  const rankingRequestedKeyRef = useRef('');
  const [loading, setLoading] = useState(true);
  const [, setLoadedTabs] = useState<MarketingTabLoadState>({});
  const [tabLoading, setTabLoading] = useState<MarketingTabLoadState>({});
  const tabLoadingRef = useRef<Set<MarketingTab>>(new Set());
  const tabLoadSequenceRef = useRef(0);
  const tabLoadTokensRef = useRef<Partial<Record<MarketingTab, number>>>({});
  const loadedTabsRef = useRef<MarketingTabLoadState>({});
  const [tabErrors, setTabErrors] = useState<Partial<Record<MarketingTab, string>>>({});
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fetchParams = useMemo(() => buildFetchParams(filters, dateBasis), [dateBasis, filters]);
  const fetchParamsCacheKey = useMemo(() => getMarketingDashboardCacheKey(fetchParams), [fetchParams]);
  const latestFetchParamsCacheKeyRef = useRef(fetchParamsCacheKey);
  const dataCacheKeyRef = useRef('');
  const filterOptionsFetchParams = useMemo(() => ({
    ...resolveDateRange(filters),
    date_basis: dateBasis,
  }), [dateBasis, filters.customEnd, filters.customStart, filters.period]);
  const filterOptionsRequestKey = useMemo(
    () => `${filterOptionsFetchParams.date_basis}:${filterOptionsFetchParams.startDate}:${filterOptionsFetchParams.endDate}:${refreshVersion}`,
    [filterOptionsFetchParams.date_basis, filterOptionsFetchParams.endDate, filterOptionsFetchParams.startDate, refreshVersion]
  );
  const requestFilterOptions = useCallback(() => {
    setFilterOptionsRequest((current) => (
      current?.key === filterOptionsRequestKey
        ? current
        : { key: filterOptionsRequestKey, params: filterOptionsFetchParams }
    ));
  }, [filterOptionsFetchParams, filterOptionsRequestKey]);
  const applyLoadedTabsPatch = useCallback((patch: MarketingTabLoadState) => {
    const next = { ...loadedTabsRef.current, ...patch };
    loadedTabsRef.current = next;
    setLoadedTabs(next);
    return next;
  }, []);
  const requestCampaignOtherLocalities = useCallback(async (
    row: MarketingRow,
    scope: CampaignAggregationScope,
  ): Promise<MarketingOtherInterestLocality[]> => {
    if (row.is_unattributed === true) return [];

    const params: MarketingFetchParams = { ...fetchParams };
    const campaignId = firstText(row, ['campaign_id'], '').trim();
    const adsetId = firstText(row, ['adset_id'], '').trim();
    const adId = firstText(row, ['ad_id'], '').trim();
    const creativeId = firstText(row, ['creative_id'], '').trim();

    if (campaignId) params.campaign_id = campaignId;
    if (scope !== 'campaign' && adsetId) params.adset_id = adsetId;
    if (scope === 'creative') {
      if (adId) params.ad_id = adId;
      if (creativeId) params.creative_id = creativeId;
    }

    if (!params.campaign_id) {
      throw new Error('A campanha da linha não possui identificador.');
    }
    if (scope !== 'campaign' && !params.adset_id) {
      throw new Error('O adset da linha não possui identificador.');
    }
    if (scope === 'creative' && !params.ad_id && !params.creative_id) {
      throw new Error('O criativo da linha não possui identificador.');
    }

    const response = await marketingRepository.fetchInterestRegions(params);
    if (!response.ok) {
      throw new Error('A API não retornou as regiões de interesse da linha.');
    }
    return response.other_localities ?? [];
  }, [fetchParams, marketingRepository]);

  useEffect(() => {
    latestFetchParamsCacheKeyRef.current = fetchParamsCacheKey;
    rankingLoadingRef.current = null;
    rankingRequestedKeyRef.current = '';
    setRankingLoadingLevel(null);
  }, [fetchParamsCacheKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;

    window.sessionStorage.setItem(
      MARKETING_DATE_FILTER_STORAGE_KEY,
      JSON.stringify({
        customEnd: filters.period === 'custom' ? filters.customEnd : '',
        customStart: filters.period === 'custom' ? filters.customStart : '',
        period: filters.period,
      })
    );
  }, [filters.customEnd, filters.customStart, filters.period]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;
    window.sessionStorage.setItem(MARKETING_DATE_BASIS_STORAGE_KEY, dateBasis);
  }, [dateBasis]);

  useEffect(() => {
    let cancelled = false;
    const requestCacheKey = fetchParamsCacheKey;

    async function loadMarketing() {
      tabLoadingRef.current.clear();
      tabLoadTokensRef.current = {};
      const cachedDashboard = readMarketingDashboardCache(fetchParams);

      if (cachedDashboard) {
        const overviewOnlyTabs = { overview: true };
        dataCacheKeyRef.current = requestCacheKey;
        loadedTabsRef.current = overviewOnlyTabs;
        setData(cachedDashboard.dashboard);
        setLoadedTabs(overviewOnlyTabs);
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        setError(null);
        if (!cachedDashboard) {
          loadedTabsRef.current = {};
          setLoadedTabs({});
        }
        setTabErrors({});
        setTabLoading({});
        const dashboard = await marketingRepository.fetchDashboard(fetchParams);
        if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
          const loadedOverviewTabs: MarketingTabLoadState = { ...loadedTabsRef.current, overview: true };
          loadedTabsRef.current = loadedOverviewTabs;
          setData((current) => {
            const canKeepCurrentTabs = dataCacheKeyRef.current === requestCacheKey;
            const baseDashboard = cachedDashboard
              ? { ...cachedDashboard.dashboard, overview: dashboard.overview }
              : dashboard;
            const dashboardForState = keepLoadedTabData(
              baseDashboard,
              canKeepCurrentTabs ? current : null,
              loadedOverviewTabs
            );

            dataCacheKeyRef.current = requestCacheKey;
            writeMarketingDashboardCache(fetchParams, dashboardForState, loadedOverviewTabs);
            return dashboardForState;
          });
          setLoadedTabs(loadedOverviewTabs);
        }
      } catch (loadError) {
        console.error('Erro ao carregar marketing:', loadError);
        if (!cancelled) {
          if (cachedDashboard) {
            const overviewOnlyTabs = { overview: true };
            dataCacheKeyRef.current = requestCacheKey;
            loadedTabsRef.current = overviewOnlyTabs;
            setData(cachedDashboard.dashboard);
            setLoadedTabs(overviewOnlyTabs);
            setError('Não foi possível atualizar os dados de marketing. Exibindo os últimos dados carregados.');
          } else {
            setData(null);
            setError('Não foi possível carregar os dados de marketing.');
          }
          tabLoadingRef.current.clear();
          setTabLoading({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadMarketing();

    return () => {
      cancelled = true;
    };
  }, [fetchParams, fetchParamsCacheKey, marketingRepository, refreshVersion]);

  const requestPerformanceRanking = useCallback(async (rankingLevel: PerformanceRankingLevel) => {
    if (rankingLevel === 'campaigns' || rankingLoadingRef.current || ((data?.overview[rankingLevel]?.length ?? 0) > 0)) return;

    const requestCacheKey = fetchParamsCacheKey;
    const requestKey = `${requestCacheKey}:${refreshVersion}:${rankingLevel}`;
    if (rankingRequestedKeyRef.current === requestKey) return;

    rankingRequestedKeyRef.current = requestKey;
    rankingLoadingRef.current = rankingLevel;
    setRankingLoadingLevel(rankingLevel);

    try {
      const overview = await marketingRepository.fetchOverview({
        ...fetchParams,
        limit: 5,
        overview_mode: 'ranking',
        ranking_level: rankingLevel,
      });

      if (latestFetchParamsCacheKeyRef.current !== requestCacheKey) return;

      setData((current) => {
        if (!current) return current;

        const nextDashboard = {
          ...current,
          overview: {
            ...current.overview,
            [rankingLevel]: overview[rankingLevel],
          },
        };

        writeMarketingDashboardCache(fetchParams, nextDashboard, loadedTabsRef.current);
        return nextDashboard;
      });
    } catch (formsError) {
      console.error('Erro ao carregar ranking de desempenho:', formsError);
      if (latestFetchParamsCacheKeyRef.current === requestCacheKey) {
        setError('Não foi possível carregar o ranking selecionado.');
      }
    } finally {
      if (latestFetchParamsCacheKeyRef.current === requestCacheKey) {
        rankingLoadingRef.current = null;
        setRankingLoadingLevel(null);
      }
    }
  }, [
    data?.overview.adsets.length,
    data?.overview.ads.length,
    data?.overview.creatives.length,
    data?.overview.forms.length,
    fetchParams,
    fetchParamsCacheKey,
    marketingRepository,
    refreshVersion,
  ]);

  useEffect(() => {
    if (filtersPopoverOpen) requestFilterOptions();
  }, [filtersPopoverOpen, requestFilterOptions]);

  const hasDashboardData = data !== null;

  useEffect(() => {
    const dashboardData = data;
    if (!dashboardData || activeTab === 'overview' || loadedTabsRef.current[activeTab] || tabLoadingRef.current.has(activeTab)) return;

    const tabToLoad = activeTab;
    const requestCacheKey = fetchParamsCacheKey;
    let cancelled = false;
    const tabLoadToken = tabLoadSequenceRef.current + 1;
    tabLoadSequenceRef.current = tabLoadToken;
    tabLoadTokensRef.current[tabToLoad] = tabLoadToken;

    async function loadActiveTab() {
      try {
        tabLoadingRef.current.add(tabToLoad);
        setTabLoading((current) => ({ ...current, [tabToLoad]: true }));
        setTabErrors((current) => {
          const next = { ...current };
          delete next[tabToLoad];
          return next;
        });

        if (tabToLoad === 'campaigns') {
          const interestRegionsRequest = marketingRepository.fetchInterestRegions(fetchParams);
          void interestRegionsRequest.then((interestRegions) => {
            if (!interestRegions.ok) throw new Error('A API não retornou as regiões de interesse.');
            if (cancelled || latestFetchParamsCacheKeyRef.current !== requestCacheKey) return;

            const nextLoadedTabs = applyLoadedTabsPatch({ interestRegions: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, interestRegions };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }).catch((interestRegionsError) => {
            console.error('Erro ao carregar regiões de interesse da tabela de campanhas:', interestRegionsError);
          });

          const campaigns = await marketingRepository.fetchRows('campaigns', fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ campaigns: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, campaigns };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'ads') {
          const ads = await marketingRepository.fetchRows('ads', fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ ads: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, ads };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'audience') {
          const ageGender = await marketingRepository.fetchRows('age-gender', fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ audience: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, ageGender };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'placement') {
          const placement = await marketingRepository.fetchRows('placement', fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ placement: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, placement };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'regions') {
          const regions = await marketingRepository.fetchRows('regions', fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ regions: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, regions };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'interestRegions') {
          const interestRegions = await marketingRepository.fetchInterestRegions(fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ interestRegions: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, interestRegions };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'creativeRegions') {
          const creativeRegions = await marketingRepository.fetchRows('creative-regions', fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ creativeRegions: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, creativeRegions };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'decision') {
          const decisionMatrix = await marketingRepository.fetchDecisionMatrix(fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ decision: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, decisionMatrix };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
          return;
        }

        if (tabToLoad === 'audit') {
          const audit = await marketingRepository.fetchAudit(fetchParams);

          if (!cancelled && latestFetchParamsCacheKeyRef.current === requestCacheKey) {
            const nextLoadedTabs = applyLoadedTabsPatch({ audit: true });
            setData((current) => {
              if (!current) return current;
              const next = { ...current, audit };
              dataCacheKeyRef.current = requestCacheKey;
              writeMarketingDashboardCache(fetchParams, next, nextLoadedTabs);
              return next;
            });
          }
        }
      } catch (loadError) {
        console.error(`Erro ao carregar aba de marketing ${tabToLoad}:`, loadError);
        if (!cancelled) {
          setTabErrors((current) => ({
            ...current,
            [tabToLoad]: 'Não foi possível carregar os dados desta aba.',
          }));
        }
      } finally {
        if (tabLoadTokensRef.current[tabToLoad] === tabLoadToken) {
          delete tabLoadTokensRef.current[tabToLoad];
          tabLoadingRef.current.delete(tabToLoad);
          setTabLoading((current) => ({ ...current, [tabToLoad]: false }));
        }
      }
    }

    void loadActiveTab();

    return () => {
      cancelled = true;
    };
  }, [activeTab, applyLoadedTabsPatch, hasDashboardData, fetchParams, fetchParamsCacheKey, marketingRepository]);

  useEffect(() => {
    if (!filterOptionsRequest) return;

    const request = filterOptionsRequest;
    let cancelled = false;
    setFilterOptionsError(false);
    setFilterOptionsLoading(true);

    async function loadFilterOptions() {
      try {
        const options = await marketingRepository.fetchFilterOptions(request.params);
        if (!cancelled) setFilterOptionsData(options);
      } catch (loadError) {
        console.error('Erro ao carregar opções dos filtros de marketing:', loadError);
        if (!cancelled) {
          setFilterOptionsData(null);
          setFilterOptionsError(true);
        }
      } finally {
        if (!cancelled) setFilterOptionsLoading(false);
      }
    }

    void loadFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [filterOptionsRequest, marketingRepository]);

  const filterOptions = useMemo<MarketingFilterOptionGroups>(() => {
    const dashboardCampaignRows = [...(data?.campaigns?.rows ?? []), ...(data?.overview.campaigns ?? [])];
    const dashboardAdsetRows = data?.overview.adsets ?? [];
    const dashboardAdsRows = data?.ads?.rows ?? [];
    const dashboardCreativeRows = data?.overview.creatives ?? [];
    const dashboardPlacementRows = data?.placement?.rows ?? [];
    const dashboardAgeRows = data?.ageGender?.rows ?? [];
    const dashboardRegionRows = [...(data?.regions?.rows ?? []), ...(data?.overview.regions ?? [])];
    const campaignRows = filterOptionsData?.campaigns ?? dashboardCampaignRows;
    const adsetRows = filterOptionsData?.adsets ?? dashboardAdsetRows;
    const adsRows = filterOptionsData?.ads ?? dashboardAdsRows;
    const creativeRows = filterOptionsData?.creatives ?? (dashboardCreativeRows.length > 0 ? dashboardCreativeRows : adsRows);
    const accountRows = filterOptionsData?.accounts ?? [...campaignRows, ...adsRows];
    const ageRows = filterOptionsData?.ages ?? dashboardAgeRows;
    const genderRows = filterOptionsData?.genders ?? dashboardAgeRows;
    const regionRows = filterOptionsData?.regions ?? dashboardRegionRows;
    const publisherPlatformRows = filterOptionsData?.publisher_platforms ?? dashboardPlacementRows;
    const platformPositionRows = filterOptionsData?.platform_positions ?? dashboardPlacementRows;
    const devicePlatformRows = filterOptionsData?.device_platforms ?? dashboardPlacementRows;
    const sourceChannelRows = filterOptionsData?.contact_sources ?? contactSourceOptions.map((option) => ({
      source_channel: option.value,
      source_channel_label: option.label,
    }));
    const formRows = filterOptionsData?.forms ?? [];
    const campaignScopedRows = rowsMatchingRelations(campaignRows, [
      ['account_id', filters.accountId],
    ]);
    const adsetScopedRows = rowsMatchingRelations(adsetRows, [
      ['account_id', filters.accountId],
      ['campaign_id', filters.campaignId],
    ]);
    const adsScopedRows = rowsMatchingRelations(adsRows, [
      ['account_id', filters.accountId],
      ['campaign_id', filters.campaignId],
      ['adset_id', filters.adsetId],
    ]);
    const creativeScopedRows = rowsMatchingRelations(creativeRows, [
      ['account_id', filters.accountId],
      ['campaign_id', filters.campaignId],
      ['adset_id', filters.adsetId],
      ['ad_id', filters.adId],
    ]);
    const formScopedRows = rowsMatchingRelations(formRows, [
      ['account_id', filters.accountId],
      ['campaign_id', filters.campaignId],
      ['adset_id', filters.adsetId],
      ['ad_id', filters.adId],
      ['source_channel', filters.sourceChannel],
    ]).map(rowWithFormFilterValue);

    return {
      accountId: optionsFromRows(accountRows, 'account_id', 'account_name', filters.accountId),
      adId: optionsFromRows(adsScopedRows, 'ad_id', 'ad_name', filters.adId),
      adsetId: optionsFromRows(adsetScopedRows, 'adset_id', 'adset_name', filters.adsetId),
      age: optionsFromRows(ageRows, 'age', undefined, filters.age, translateAgeLabel),
      campaignId: optionsFromRows(campaignScopedRows, 'campaign_id', 'campaign_name', filters.campaignId),
      creativeId: optionsFromRows(creativeScopedRows, 'creative_id', 'creative_title', filters.creativeId),
      devicePlatform: optionsFromRows(devicePlatformRows, 'device_platform', undefined, filters.devicePlatform),
      formId: optionsFromRows(formScopedRows, 'form_filter_value', 'form_name', filters.formId, undefined, false),
      gender: optionsFromRows(genderRows, 'gender', undefined, filters.gender, translateGenderLabel),
      platformPosition: optionsFromRows(platformPositionRows, 'platform_position', undefined, filters.platformPosition),
      publisherPlatform: optionsFromRows(publisherPlatformRows, 'publisher_platform', undefined, filters.publisherPlatform),
      region: optionsFromRows(regionRows, 'region', undefined, filters.region),
      sourceChannel: optionsFromRows(sourceChannelRows, 'source_channel', 'source_channel_label', filters.sourceChannel),
    };
  }, [data, filterOptionsData, filters]);

  useEffect(() => {
    if (!filterOptionsData || !filters.formId) return;
    const hasSelectedForm = filterOptions.formId.some((option) => option.value === filters.formId);
    if (hasSelectedForm) return;

    setFilters((current) => (current.formId === filters.formId ? { ...current, formId: '' } : current));
  }, [filterOptions.formId, filterOptionsData, filters.formId]);

  const activeFilterItems = useMemo(() => buildActiveFilterItems(filters, filterOptions), [filterOptions, filters]);
  const hasFilters = useMemo(() => {
    const comparableFilters = { ...filters, period: filters.period === '30d' ? '' : filters.period };
    return Object.values(comparableFilters).some((value) => value !== '');
  }, [filters]);
  const activeDimensionFilterItems = useMemo(
    () => activeFilterItems.filter((item) => item.key !== 'period'),
    [activeFilterItems]
  );
  const hasDimensionFilters = activeDimensionFilterItems.length > 0;

  useCloseOnOutsideInteraction(filtersPopoverOpen, filtersPopoverRef, () => setFiltersPopoverOpen(false));

  function updateFilterValue<Key extends keyof MarketingFilterState>(key: Key, value: MarketingFilterState[Key]) {
    setFilters((current) => {
      const next = { ...current, [key]: value };

      if (key === 'accountId') {
        next.campaignId = '';
        next.adsetId = '';
        next.adId = '';
        next.creativeId = '';
      }
      if (key === 'campaignId') {
        next.adsetId = '';
        next.adId = '';
        next.creativeId = '';
      }
      if (key === 'adsetId') {
        next.adId = '';
        next.creativeId = '';
      }
      if (key === 'adId') {
        next.creativeId = '';
      }
      if (key === 'sourceChannel' && value !== 'lead_ads') {
        next.formId = '';
      }
      if (key === 'formId' && value) {
        next.sourceChannel = 'lead_ads';
      }

      return next;
    });
  }

  function clearOneFilter(key: keyof MarketingFilterState) {
    if (key === 'period') {
      clearDateFilter();
      return;
    }

    updateFilterValue(key, '' as MarketingFilterState[typeof key]);
  }

  function applyDateFilter(nextDateFilter: Pick<MarketingFilterState, 'customEnd' | 'customStart' | 'period'>) {
    setFilters((current) => ({
      ...current,
      ...nextDateFilter,
    }));
  }

  function clearDateFilter() {
    setFilters((current) => ({
      ...current,
      customEnd: '',
      customStart: '',
      period: '30d',
    }));
  }

  function clearAllFilters() {
    setFilters(initialFilters);
  }

  function clearDimensionFilterValues() {
    setFilters((current) => clearDimensionFilters(current));
  }

  function refreshMarketing() {
    setRefreshVersion((current) => current + 1);
  }

  const activeTabLabel = tabs.find((tab) => tab.key === activeTab)?.label ?? 'Marketing';
  const activeTabDataLoading = tabLoading[activeTab] === true;
  const activeTabLoading = activeTabDataLoading;
  const activeTabError = tabErrors[activeTab];

  return (
    <section className="w-full space-y-5">
      <div className={classNames(habitaSurfaceClass, 'space-y-3 p-3 sm:p-4')}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0 overflow-x-auto">
            <div className={`inline-flex min-w-max rounded-lg border ${habitaControlBorderClass} bg-card p-1`}>
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={
                    activeTab === tab.key
                      ? 'inline-flex h-9 items-center rounded-md bg-primary px-3 font-instrument text-sm font-medium text-primary-foreground'
                      : 'inline-flex h-9 items-center rounded-md px-3 font-instrument text-sm font-medium text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary'
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <HabitaDateRangeFilter
              dateBasis={dateBasis}
              onApply={applyDateFilter}
              onClear={clearDateFilter}
              onDateBasisChange={setDateBasis}
              showDateBasis
              value={filters}
            />
            <div ref={filtersPopoverRef} className="relative">
              <HabitaButton
                aria-expanded={filtersPopoverOpen}
                aria-haspopup="dialog"
                icon="ri-equalizer-line"
                onClick={() => setFiltersPopoverOpen((current) => !current)}
                size="sm"
                className="h-9"
              >
                Filtros
                {activeDimensionFilterItems.length > 0 && (
                  <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">
                    {activeDimensionFilterItems.length}
                  </span>
                )}
              </HabitaButton>
              <MarketingFiltersPopover
                filters={filters}
                hasActiveDimensions={hasDimensionFilters}
                onChange={updateFilterValue}
                onClearDimensions={clearDimensionFilterValues}
                onClearOne={clearOneFilter}
                open={filtersPopoverOpen}
                options={filterOptions}
              />
            </div>
            <HabitaButton
              icon={loading || activeTabLoading || filterOptionsLoading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}
              onClick={refreshMarketing}
              size="sm"
              variant="secondary"
              disabled={loading || activeTabLoading}
            >
              Atualizar
            </HabitaButton>
            {filterOptionsError && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-500/10 px-2 py-1 font-instrument text-[11px] text-amber-800 dark:text-amber-100">
                <i className="ri-alert-line" aria-hidden />
                Opções parciais
              </span>
            )}
          </div>
        </div>

        {activeFilterItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-instrument text-xs font-medium text-textSecondary">Ativos:</span>
            {activeFilterItems.map((item) => (
              <HabitaActiveFilterChip
                key={`${item.key}-${item.value}`}
                label={item.label}
                value={item.value}
                onRemove={() => clearOneFilter(item.key)}
              />
            ))}
            {hasFilters && (
              <HabitaButton icon="ri-close-line" onClick={clearAllFilters} size="sm" variant="ghost">
                Limpar tudo
              </HabitaButton>
            )}
          </div>
        )}
      </div>

      {error && <HabitaErrorNotice>{error}</HabitaErrorNotice>}
      {activeTabError && <HabitaErrorNotice>{activeTabError}</HabitaErrorNotice>}

      {loading ? (
        <MarketingLoadingState />
      ) : data ? (
        <div aria-label={activeTabLabel}>
          {activeTabDataLoading ? (
            <MarketingLoadingState />
          ) : (
            <>
              {activeTab === 'overview' && (
                <OverviewTab
                  onRequestRanking={requestPerformanceRanking}
                  overview={data.overview}
                  rankingLoadingLevel={rankingLoadingLevel}
                />
              )}
              {activeTab === 'campaigns' && (
                <CampaignsAdsetsPrototypeTab
                  adsRows={data.ads.rows}
                  attributionModel={data.campaigns.attribution_model}
                  interestRegions={data.interestRegions}
                  interestRegionsLoaded={loadedTabsRef.current.interestRegions === true}
                  onRequestOtherLocalities={requestCampaignOtherLocalities}
                  rows={data.campaigns.rows}
                  summary={data.overview.summary}
                />
              )}
              {activeTab === 'ads' && <AdsCreativePrototypeTab rows={data.ads.rows} />}
              {activeTab === 'audience' && <AudiencePrototypeTab ageGender={data.ageGender} />}
              {activeTab === 'interestRegions' && <InterestRegionsTab data={data.interestRegions} />}
              {activeTab === 'placement' && <PlacementPrototypeTab placement={data.placement} />}
              {activeTab === 'regions' && <RegionsPrototypeTab regions={data.regions} />}
              {activeTab === 'creativeRegions' && <CreativeRegionsPrototypeTab creativeRegions={data.creativeRegions} />}
              {activeTab === 'decision' && <DecisionPrototypeTab matrix={data.decisionMatrix} />}
              {activeTab === 'audit' && <AuditTab audit={data.audit} />}
            </>
          )}
        </div>
      ) : (
        <HabitaEmptyState
          icon="ri-megaphone-line"
          title="Nenhum dado de marketing disponível"
          description="Os dados aparecerão aqui quando a API retornar informações para o período."
        />
      )}
    </section>
  );
}

function LabeledSelect({
  className,
  label,
  onChange,
  onClear,
  options,
  value,
}: {
  className?: string;
  label: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  options: SelectOption[];
  value: string;
}) {
  const hasSelection = value.trim() !== '';
  const clearLabel = `Limpar seleção de ${label.toLowerCase()}`;

  return (
    <div className="flex w-full min-w-0 items-center gap-1">
      <label className="min-w-0 flex-1">
        <span className="sr-only">{label}</span>
        <HabitaSelect
          value={selectedFilterValue(value)}
          onChange={(event) => onChange(filterValueFromSelect(event.target.value))}
          options={optionsWithDefault(label, options)}
          className={classNames('w-[160px]', className)}
          aria-label={label}
        />
      </label>

      {onClear && hasSelection && (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md px-2 font-instrument text-xs font-medium text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/20"
          aria-label={clearLabel}
          title="Limpar seleção"
        >
          <i className="ri-close-line text-sm" aria-hidden />
          Limpar seleção
        </button>
      )}
    </div>
  );
}
